import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowRightIcon,
  CheckIcon,
  DocumentTextIcon,
  ClipboardDocumentListIcon,
  ShieldCheckIcon,
  ArrowUpTrayIcon,
  XMarkIcon,
  ClockIcon,
  UserIcon,
  AcademicCapIcon,
  VideoCameraIcon,
  MicrophoneIcon,
  SparklesIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  ArrowPathIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  EyeIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { reportDiag, reportPageOpen, postWithDiag } from '../../lib/candidateDiag';
import { snapshotFile } from '../../lib/fileSnapshot';
import { fitImageForStorage } from '../../utils/documentImageCompression';
import { usePreventBack } from '../../hooks/usePreventBack';
import { applicationFormPath } from '../../lib/screeningApplySession';
import { useScreeningLeaveWarning } from '../../hooks/useScreeningLeaveWarning';
import { useScreeningExpiryRedirect } from '../../hooks/useScreeningExpiryRedirect';
import LeaveScreeningModal from '../../components/ui/LeaveScreeningModal';
import DocumentStatusCard from '../../components/screenings/DocumentStatusCard';
import DocumentPreviewModal from '../../components/screenings/DocumentPreviewModal';
import BankDetailsForm, {
  MIN_ACCOUNT_DIGITS,
  MAX_ACCOUNT_DIGITS,
  accountDigits,
  isValidAccountNumber,
  isValidIfsc,
  isValidHolderName,
  isBankFormComplete,
  normalizeIfscInput,
} from '../../components/screenings/BankDetailsForm';
import {
  buildDocumentStatus,
  candidateWarningsFrom,
  checkDocumentTypeMatch,
  formatFieldList,
  isBankDoc,
  DOC_PROBLEM,
  nameMatchSummary,
} from '../../lib/documentStatus';

const STEP_SECONDARY = {
  'Basic details': 'Share your contact information',
  'Resume upload': 'Upload your latest resume',
  'Document upload': 'Upload the requested documents',
  Assessment: 'Complete skills assessment',
  'AI interview': 'Answer structured questions',
};

const getStepMeta = (interviewType) => ({
  'Basic details': { icon: UserIcon, desc: '', gradient: 'from-blue-500 to-indigo-600' },
  'Resume upload': { icon: DocumentTextIcon, desc: '', gradient: 'from-electric-blue to-indigo-600' },
  'Document upload': { icon: DocumentTextIcon, desc: '', gradient: 'from-electric-blue to-indigo-600' },
  'Application form': { icon: ClipboardDocumentListIcon, desc: '', gradient: 'from-electric-blue to-indigo-600' },
  Assessment: { icon: AcademicCapIcon, desc: '', gradient: 'from-electric-blue to-indigo-600' },
  'AI interview': {
    icon: interviewType === 'video' ? VideoCameraIcon : MicrophoneIcon,
    desc: '',
    gradient: 'from-electric-blue to-indigo-600',
  },
});

function getInterviewStepLabel(interviewType) {
  if (interviewType === 'video') return 'AI video interview';
  if (interviewType === 'voice' || interviewType === 'audio') return 'AI voice interview';
  return 'AI interview';
}

const UPLOAD_HINTS = {
  'resume::resume_cv':
    'Upload your latest resume / CV as a PDF or high-resolution image (JPEG/PNG).',
  'education::degree_certificate':
    'Provide a legible copy of your degree certificate. PDF or high-resolution image (JPEG/PNG).',
  'education::marksheet':
    'Provide a legible copy of your marksheet. Ensure all pages are readable.',
  'education::provisional_certificate':
    'Provide your provisional certificate if the final degree certificate is not yet available.',
  'employment::experience_letter':
    'Upload a clear copy of your experience letter, relieving letter, or service certificate, as applicable.',
  'employment::offer_letter':
    'Upload a clear copy of your offer letter from the relevant employer.',
  'employment::uan_number':
    'Declare your 12-digit EPFO Universal Account Number or confirm if you are a fresher / non-EPFO member.',
  'salary::salary_slips':
    'Upload salary slips for the period requested. Combine multiple pages into one PDF where possible.',
  'salary::form_16':
    'Upload Form 16 or equivalent tax statement as a legible PDF or image.',
  'address::utility_bill':
    'Upload a recent utility bill showing your name and address (electricity, water, or gas, as accepted by the employer).',
  'address::rental_agreement':
    'Upload a legible copy of your rental agreement or equivalent address proof.',
  'professional::professional_license':
    'Upload a clear copy of your professional license or certification.',
  'govt_id::aadhaar_card':
    'Upload your Aadhaar Card as a photo (JPEG/PNG) or a PDF — the UIDAI e-Aadhaar download works. Ensure all details are visible.',
  'govt_id::pan_card':
    'Upload your PAN Card as a photo (JPEG/PNG) or a PDF. Ensure the PAN number is visible.',
  'govt_id::aadhaar_or_pan':
    'Upload your Aadhaar or PAN Card as a photo (JPEG/PNG) or a PDF.',
  'govt_id::passport':
    'Upload a clear, legible file of your Passport in PDF, JPEG, or PNG format (maximum size as permitted by the portal).',
  'govt_id::driving_licence':
    'Upload a clear, legible file of your Driving Licence in PDF, JPEG, or PNG format (maximum size as permitted by the portal).',
  // Deliberately lists four unrelated formats: nothing here checks what the document
  // looks like, only that your name, account number and IFSC are all readable on it.
  'bank_details::bank_account':
    'Upload anything that shows your name, account number and IFSC code together — a cancelled cheque, a passbook page, a bank statement, or your bank app’s account details screen. Photo, screenshot or PDF all work.',
};

function getUploadHint(doc) {
  const k = `${doc.type}::${doc.subtype}`;
  return (
    UPLOAD_HINTS[k] ||
    'Upload a clear, legible file in PDF, JPEG, or PNG format (maximum size as permitted by the portal).'
  );
}

/**
 * Client-side downsampling, so a 4MB camera photo does not take six seconds to upload.
 * The original file is still what goes to S3; this copy exists only to be read.
 *
 * The cap was 1400px, and that was silently the largest single cause of failed ID reads.
 * An Aadhaar's name and DOB are small print inside an already small card: measured on the
 * pilot's cards, the same photo of the same Aadhaar through the same OCR gave
 *
 *   1400px -> no name, no DOB    ("12 34 567 TEMPLESTREEI PINCod")
 *   2000px -> DOB only
 *   2600px -> name and DOB       ("R ANITA SHARMA D/O: ...")
 *
 * The candidate was then told to re-upload "a clearer photo" of a photo that was already
 * clear enough — the detail was thrown away in their own browser, before anything saw it.
 * Re-uploading cannot fix that, so the loop had no exit.
 *
 * Within the cap the file is now passed through untouched rather than re-encoded: a JPEG
 * round-trip at quality 0.85 costs detail and buys nothing when no resize is needed.
 */
function compressImageForVerification(file, maxDimension = 2600, quality = 0.92) {
  return new Promise((resolve) => {
    if (!file || !file.type?.startsWith('image/') || file.type === 'image/svg+xml') {
      return resolve(file);
    }
    // If file is already lightweight (<= 400KB), send directly
    if (file.size <= 400 * 1024) {
      return resolve(file);
    }

    const img = new Image();
    const reader = new FileReader();

    reader.onload = (e) => {
      img.onload = () => {
        let { width, height } = img;
        // Already small enough: re-encoding would only lose detail.
        if (width <= maxDimension && height <= maxDimension) {
          return resolve(file);
        }
        if (width > maxDimension || height > maxDimension) {
          if (width > height) {
            height = Math.round((height * maxDimension) / width);
            width = maxDimension;
          } else {
            width = Math.round((width * maxDimension) / height);
            height = maxDimension;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              const compressedFile = new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), {
                type: 'image/jpeg',
                lastModified: Date.now(),
              });
              resolve(compressedFile);
            } else {
              resolve(file);
            }
          },
          'image/jpeg',
          quality
        );
      };
      img.onerror = () => resolve(file);
      img.src = e.target.result;
    };
    reader.onerror = () => resolve(file);
    reader.readAsDataURL(file);
  });
}

/** Steps that record a typed answer rather than a document — nothing to view or replace. */
const DECLARATION_SUBTYPES = ['uan_number', 'marital_status', 'complete_address'];

function isFileBackedDoc(doc) {
  return !DECLARATION_SUBTYPES.includes(doc?.subtype);
}

// checkDocumentTypeMatch, isBankDoc, formatFieldList and the Aadhaar field rules moved to
// lib/documentStatus.js, next to the decision that uses them. They were duplicated across
// three render branches here, and the branch that was never written — bank — is what put
// an "Invalid Document" card under every successful bank upload.

// Account-number, IFSC and holder-name rules live with the bank form that applies them.

export default function ScreeningDocumentUpload() {
  const { screeningId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get('token') || '';
  const emailFromQuery = searchParams.get('email') || '';
  const nameFromQuery = searchParams.get('name') || '';

  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const [applyInfo, setApplyInfo] = useState(null);
  const [docRequestData, setDocRequestData] = useState(null);
  const [uploadingKeys, setUploadingKeys] = useState(new Set());
  const [submittingAll, setSubmittingAll] = useState(false);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState({});
  const [documentNumbers, setDocumentNumbers] = useState({});
  const [bankConfirmations, setBankConfirmations] = useState({});
  // IFSC and the name on the account, per bank document key; the account number lives in
  // documentNumbers like every other document's number.
  const [bankFields, setBankFields] = useState({});
  const [ocrEditableFields, setOcrEditableFields] = useState({});
  const [panChoices, setPanChoices] = useState({});
  const [timeLeftSeconds, setTimeLeftSeconds] = useState(30 * 60);
  const [verifyingKeys, setVerifyingKeys] = useState(new Set());
  const [verificationResults, setVerificationResults] = useState({});
  // What is wrong with each document, as buildDocumentStatus decided it. Keyed the same
  // way as every other per-document map; null/absent means nothing is wrong.
  const [docStatuses, setDocStatuses] = useState({});
  // The one message that belongs to the step rather than to a document — "you have not
  // answered this yet", "the submit failed". It used to be a toast; it is now rendered
  // next to the button that produced it, which is where somebody looks for it.
  const [pageAlert, setPageAlert] = useState(null);
  const [preview, setPreview] = useState(null);
  // Documents the candidate has chosen to redo. An accepted document renders its summary
  // rather than its controls, so re-opening the controls needs to be explicit state —
  // uploadedKeys still says it is done, and that is still true until the new file lands.
  const [replacingKeys, setReplacingKeys] = useState(new Set());

  // ── Multi-step wizard state ──
  const [currentDocIndex, setCurrentDocIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const prevUploadedKeysRef = useRef(new Set());
  const hasInitializedStepRef = useRef(false);

  const fileInputRefs = useRef({});
  const [showLeaveModal, setShowLeaveModal] = useState(false);

  const alertRef = useRef(null);
  const previewUrlsRef = useRef({});

  /**
   * Raise the step-level message.
   *
   * `tick` is here because the same message can be raised twice in a row — pressing a
   * disabled-in-spirit Next button repeatedly — and without it React sees identical state
   * and the alert never re-announces or re-scrolls, so the second press looks like the
   * page ignored it.
   */
  const raiseAlert = useCallback((message, tone = 'error') => {
    setPageAlert({ message, tone, tick: Date.now() });
  }, []);

  const clearAlert = useCallback(() => setPageAlert(null), []);

  // Clear a rejected upload and immediately reopen the file picker, so a
  // candidate told to re-shoot their card lands straight in the camera.
  const resetUploadFor = useCallback((key) => {
    setSelectedFiles((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setVerificationResults((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setDocStatuses((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    if (fileInputRefs.current[key]) {
      fileInputRefs.current[key].value = '';
      fileInputRefs.current[key].click();
    }
  }, []);

  /**
   * Hold on to a local preview of the file the candidate just picked.
   *
   * selectedFiles is cleared the moment a document uploads, which is correct — it is the
   * queue for submit, not a record of what was sent. But the candidate still needs to be
   * able to look at what they sent, so the bytes are kept here separately and released
   * only when they are replaced or the page goes away. Object URLs leak until revoked.
   */
  const rememberPreview = useCallback((key, file) => {
    if (!file) return;
    const previous = previewUrlsRef.current[key];
    if (previous?.url) URL.revokeObjectURL(previous.url);
    previewUrlsRef.current[key] = {
      url: URL.createObjectURL(file),
      fileName: file.name || 'document',
      mimeType: file.type || '',
    };
  }, []);

  useEffect(() => {
    const urls = previewUrlsRef.current;
    return () => {
      Object.values(urls).forEach((entry) => entry?.url && URL.revokeObjectURL(entry.url));
    };
  }, []);

  // A complaint raised on one document has nothing to say about the next one.
  useEffect(() => {
    setPageAlert(null);
  }, [currentDocIndex]);

  // Keyed on tick, not on the message, so pressing Next twice on the same unmet
  // requirement moves the viewport both times instead of looking ignored.
  useEffect(() => {
    if (!pageAlert || !alertRef.current) return;
    alertRef.current.focus({ preventScroll: true });
    alertRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [pageAlert?.tick]);

  /**
   * Show the candidate the document behind this step.
   *
   * Local bytes first — instant, and it works with no network. A refresh throws those
   * away without throwing away the upload, so fall back to a presigned URL scoped to
   * this candidate's token.
   */
  const openPreview = useCallback(async (doc) => {
    const key = `${doc.type}::${doc.subtype}`;
    const local = previewUrlsRef.current[key];
    if (local?.url) {
      setPreview({ open: true, source: local, fileName: local.fileName, loading: false, error: '' });
      return;
    }

    const submission = (docRequestData?.uploadedDocuments || []).find(
      (d) => d.type === doc.type && d.subtype === doc.subtype
    );
    if (!submission?.id) {
      setPreview({
        open: true, source: null, fileName: doc.label, loading: false,
        error: 'This document is not available to preview.',
      });
      return;
    }

    setPreview({ open: true, source: null, fileName: doc.label, loading: true, error: '' });
    try {
      const res = await axios.get(`/api/documents/upload/${token}/view/${submission.id}`);
      setPreview({
        open: true,
        source: { url: res.data?.url, mimeType: res.data?.mimeType || '' },
        fileName: res.data?.fileName || doc.label,
        loading: false,
        error: '',
      });
    } catch (err) {
      setPreview({
        open: true, source: null, fileName: doc.label, loading: false,
        error: err?.response?.data?.message || 'We could not load this document just now. Please try again.',
      });
    }
  }, [docRequestData, token]);

  /**
   * Start over on a document that has already been accepted.
   *
   * The server keeps every submission and reads the newest per type/subtype, so a
   * replacement needs no delete — uploading again supersedes it. Every trace of the old
   * one is dropped from local state first so no stale OCR result can be read back
   * against the new file.
   */
  const hasPreviewableFile = useCallback((doc) => {
    if (!isFileBackedDoc(doc)) return false;
    const record = (docRequestData?.uploadedDocuments || []).find(
      (d) => d.type === doc.type && d.subtype === doc.subtype
    );
    // Fall back to true for a response from an older server that does not report it:
    // the preview then fails with its own message rather than the button vanishing.
    return record ? record.hasFile !== false : true;
  }, [docRequestData]);

  /**
   * Back out of a replacement.
   *
   * Needed because the browser tells us nothing when a file dialog is dismissed: the
   * candidate presses Replace, changes their mind, and the step is left demanding a file
   * for a document the server already has. Without a way back, Next refuses to move and
   * the only escape is to upload something.
   */
  const cancelReplace = useCallback((doc) => {
    const key = `${doc.type}::${doc.subtype}`;
    setReplacingKeys((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
    setDocStatuses((prev) => { const next = { ...prev }; delete next[key]; return next; });
    setVerificationResults((prev) => { const next = { ...prev }; delete next[key]; return next; });
    setSelectedFiles((prev) => { const next = { ...prev }; delete next[key]; return next; });
    if (fileInputRefs.current[key]) fileInputRefs.current[key].value = '';
  }, []);

  const replaceUploadedDoc = useCallback((doc) => {
    const key = `${doc.type}::${doc.subtype}`;
    setPageAlert(null);
    setVerificationResults((prev) => { const next = { ...prev }; delete next[key]; return next; });
    setDocStatuses((prev) => { const next = { ...prev }; delete next[key]; return next; });
    setDocumentNumbers((prev) => { const next = { ...prev }; delete next[key]; return next; });
    setSelectedFiles((prev) => { const next = { ...prev }; delete next[key]; return next; });
    setReplacingKeys((prev) => new Set(prev).add(key));
    if (fileInputRefs.current[key]) {
      fileInputRefs.current[key].value = '';
      fileInputRefs.current[key].click();
    }
  }, []);

  usePreventBack(true, { onBackAttempt: useCallback(() => setShowLeaveModal(true), []) });
  useScreeningLeaveWarning(!!screeningId);
  useScreeningExpiryRedirect({
    screeningId,
    email: emailFromQuery,
    enabled: !!screeningId,
  });

  const refreshRequestData = async () => {
    if (!token) return;
    try {
      const res = await axios.get(`/api/documents/upload/${token}`);
      setDocRequestData(res.data);
    } catch (err) {
      setErrorMsg(
        err?.response?.data?.message ||
        'This link has expired or is invalid. If you need assistance, contact the employer or support@talentcrux.ai.'
      );
    }
  };

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        setLoading(true);
        // 1. Fetch general apply info
        const qStr = emailFromQuery ? `?email=${encodeURIComponent(emailFromQuery)}` : '';
        const applyRes = await axios.get(`/api/screenings/${screeningId}/apply-info${qStr}`);
        if (!cancelled) setApplyInfo(applyRes.data);

        // 2. Fetch specific doc request data using BGV token
        if (token) {
          const docRes = await axios.get(`/api/documents/upload/${token}`);
          if (!cancelled) setDocRequestData(docRes.data);
        } else {
          if (!cancelled) setErrorMsg('Secure document collection token not found. Please start from the screening link.');
        }
      } catch (err) {
        if (!cancelled) {
          setErrorMsg(err?.response?.data?.message || 'Failed to initialize document upload.');
          // Without this the candidate sees a dead page and we see nothing:
          // this failure never reaches a route that logs.
          reportDiag(
            screeningId,
            'documents',
            'init_failed',
            {
              hasToken: !!token,
              httpStatus: err?.response?.status ?? null,
              errorCode: err?.code || null,
              errorMessage: String(err?.message || '').slice(0, 120),
              isNetworkFailure: !err?.response,
            },
            { email: emailFromQuery, name: nameFromQuery }
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (screeningId && token) {
      fetchData();
    } else if (screeningId) {
      // Landing here without a token is a routing/link bug, not candidate error.
      reportDiag(
        screeningId,
        'documents',
        'init_missing_token',
        { hasEmail: !!emailFromQuery },
        { email: emailFromQuery, name: nameFromQuery }
      );
    }
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screeningId, token, emailFromQuery, nameFromQuery]);

  // One capability/context baseline per page view, so any later failure on this
  // device can be compared against a device where the step works.
  useEffect(() => {
    if (!screeningId) return;
    reportPageOpen('documents', { email: emailFromQuery, name: nameFromQuery });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screeningId, emailFromQuery, nameFromQuery]);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeftSeconds((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const isExpired = timeLeftSeconds <= 0;

  const requestedDocs = docRequestData?.requestedDocuments || [];
  const uploadedDocs = docRequestData?.uploadedDocuments || [];

  /**
   * "I don't have a PAN yet" declaration — enabled per-org by the server, which owns the
   * gate (see server lib/panAcknowledgement.js). This page deliberately does not recognise
   * the client by name: `companyName` is free text an admin can rename at any time.
   *
   * When on, the PAN step asks Yes/No first. Yes is the unchanged upload + OCR + PAN-number
   * path; No collects the PAN application acknowledgement number and stores no file.
   */
  const panAckEnabled = docRequestData?.allowPanAcknowledgement === true;
  const isPanDeclarationDoc = useCallback(
    (doc) => panAckEnabled && doc?.subtype === 'pan_card',
    [panAckEnabled]
  );
  /** True once the candidate has answered "No" — the branch that replaces the file upload. */
  const isPanAckBranch = useCallback(
    (doc, key) => isPanDeclarationDoc(doc) && panChoices[key] === 'no',
    [isPanDeclarationDoc, panChoices]
  );
  // Mirrors the server's sanity guard — no issuer format is enforced, because Protean prints
  // 15 digits and UTIITSL prints a ~10-character coupon and the candidate cannot tell us which.
  const isValidPanAck = (val) => /^[A-Z0-9]{8,20}$/.test(String(val || '').replace(/[\s-]/g, '').toUpperCase());

  const uploadedKeys = useMemo(() => {
    const s = new Set();
    uploadedDocs.forEach((d) => s.add(`${d.type}::${d.subtype}`));
    return s;
  }, [uploadedDocs]);

  const pendingDocs = useMemo(() => {
    return requestedDocs.filter((d) => !uploadedKeys.has(`${d.type}::${d.subtype}`));
  }, [requestedDocs, uploadedKeys]);

  const hasAnyRequiredDocs = requestedDocs.some((d) => d.required !== false);
  const hasPendingRequiredDocs = pendingDocs.some((d) => d.required !== false);

  /** The bank form's current values for one document. */
  const bankFormValue = (key) => ({
    account: documentNumbers[key] || '',
    ifsc: bankFields[key]?.ifsc || '',
    holderName: bankFields[key]?.holderName || '',
    ifscLookup: bankFields[key]?.ifscLookup || null,
  });

  /**
   * The ID read with the name / DOB the candidate confirmed, for both upload paths.
   * `fromDocument` keeps what the card showed: the server records any edit beside it and
   * flags a different name or date for the recruiter. Copied, never mutated — a retried
   * upload must still know the original read.
   */
  const govtIdPayload = (key, vRes, docNum) => {
    const read = (vRes.validation ? vRes.validation.normalizedData : vRes.normalizedData) || null;
    const payload = vRes.validation ? { ...vRes.validation, nameMatch: vRes.nameMatch || null } : { ...vRes };
    const editable = ocrEditableFields[key];
    if (read) payload.normalizedData = { ...read };
    if (editable) {
      if (read) payload.fromDocument = { name: read.name || '', dob: read.dob || '' };
      if (!payload.normalizedData) payload.normalizedData = {};
      if (editable.name !== undefined) payload.normalizedData.name = editable.name;
      if (editable.dob !== undefined) payload.normalizedData.dob = editable.dob;
    }
    if (docNum && payload.normalizedData) payload.normalizedData.id_number = docNum;
    return payload;
  };

  /**
   * The bank read and the candidate's confirmed details, for both upload paths. The
   * fallback upload used to send no bank read at all, so a document uploaded that way
   * reached the server unverifiable.
   */
  const appendBankFields = (fd, key, verificationData) => {
    const vRes = verificationData || verificationResults[key];
    if (vRes?.bank || vRes?.bankCheck) {
      fd.append('verificationDetails', JSON.stringify({
        bank: vRes.bank || null,
        bankCheck: vRes.bankCheck || null,
        nameMatch: vRes.nameMatch || null,
      }));
    }
    fd.append('bankIfsc', normalizeIfscInput(bankFields[key]?.ifsc));
    fd.append('bankHolderName', String(bankFields[key]?.holderName || '').trim());
  };

  const toUpload = useMemo(() => {
    return pendingDocs.filter((doc) => {
      const key = `${doc.type}::${doc.subtype}`;
      if (doc.subtype === 'uan_number') {
        return /^\d{12}$/.test((documentNumbers[key] || '').replace(/\s+/g, ''));
      }
      if (isPanAckBranch(doc, key)) {
        return isValidPanAck(documentNumbers[key]);
      }
      if (doc.subtype === 'marital_status') {
        const val = documentNumbers[key];
        return val === 'Single' || val === 'Married';
      }
      if (doc.subtype === 'complete_address') {
        return !!(documentNumbers[key] && documentNumbers[key].trim().length > 5);
      }
      // A bank document is only ready once the candidate has confirmed every field the
      // server requires — account number, IFSC and the name on the account.
      if (isBankDoc(doc)) {
        return !!selectedFiles[key] && isBankFormComplete(bankFormValue(key));
      }
      return !!selectedFiles[key];
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingDocs, selectedFiles, documentNumbers, bankFields, isPanAckBranch]);

  /**
   * Required documents that are still neither uploaded nor selected.
   *
   * A requirement counts as satisfied when the document is already stored OR the
   * candidate has picked a file for it in this session — the pending pick is
   * uploaded by this same submit, so blocking on it would make the button
   * impossible to press.
   */
  const unmetRequiredDocs = useMemo(() => {
    return pendingDocs.filter((doc) => {
      if (doc.required === false) return false;
      const key = `${doc.type}::${doc.subtype}`;
      if (doc.subtype === 'uan_number') {
        if (/^\d{12}$/.test((documentNumbers[key] || '').replace(/\s+/g, ''))) return false;
        return true;
      }
      // An unanswered PAN question still counts as unmet, so the step cannot be walked past.
      if (isPanDeclarationDoc(doc)) {
        if (panChoices[key] === 'no') return !isValidPanAck(documentNumbers[key]);
        if (panChoices[key] === 'yes') return !selectedFiles[key];
        return true;
      }
      if (doc.subtype === 'marital_status') {
        const val = documentNumbers[key];
        return !(val === 'Single' || val === 'Married');
      }
      if (doc.subtype === 'complete_address') {
        return !(documentNumbers[key] && documentNumbers[key].trim().length > 5);
      }
      if (isBankDoc(doc)) {
        return !selectedFiles[key] || !isBankFormComplete(bankFormValue(key));
      }
      return !selectedFiles[key];
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingDocs, selectedFiles, documentNumbers, bankFields, isPanDeclarationDoc, panChoices]);

  // Documents gate the assessment and interview, so the step cannot be finished
  // with a required document missing. Note this is a UX guard only: the server
  // remains the authority (and logs every rejection), matching how the rest of
  // the candidate journey validates.
  const hasUnmetRequired = unmetRequiredDocs.length > 0;

  /**
   * Eagerly upload a single document to S3 right after file selection (or after
   * successful OCR for govt_id). This makes the final "Submit" button instant
   * because there is nothing left to upload — only the /complete call remains.
   *
   * On success the file is removed from selectedFiles and docRequestData is
   * refreshed so uploadedKeys picks up the newly stored doc.
   */
  const autoUploadDoc = async (doc, file, docNum, verificationData) => {
    const key = `${doc.type}::${doc.subtype}`;

    setUploadingKeys((prev) => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });

    try {
      const fd = new FormData();
      if (file) {
        fd.append('file', file);
      }
      fd.append('type', doc.type);
      fd.append('subtype', doc.subtype);
      fd.append('label', doc.label);
      if (docNum) {
        fd.append('documentNumber', docNum);
      }
      if (doc.subtype === 'uan_number') {
        fd.append('hasUan', docNum === 'NOT_APPLICABLE' ? 'false' : 'true');
      }
      // Only sent on the "no PAN yet" branch; its absence keeps the ordinary PAN upload
      // on the server's unchanged govt-ID path.
      if (isPanAckBranch(doc, key)) {
        fd.append('hasPan', 'false');
      }
      const vRes = verificationData || verificationResults[key];
      if (isBankDoc(doc)) {
        // A bank read has no doc_type and no normalized_data — it is parsed fields plus
        // the reasons we are or are not confident in them, and the server re-checks the
        // candidate's confirmed details against it.
        appendBankFields(fd, key, vRes);
      } else if (vRes?.validation || vRes?.normalizedData) {
        fd.append('verificationDetails', JSON.stringify(govtIdPayload(key, vRes, docNum)));
      }
      fd.append('consentGiven', 'true');
      fd.append('consentIp', '');

      await postWithDiag(`/api/documents/upload/${token}`, fd, {
        screeningId,
        stage: 'documents',
        event: 'auto_upload',
        identity: { email: emailFromQuery, name: nameFromQuery },
        detail: {
          docType: doc.type,
          docSubtype: doc.subtype,
          fileName: String(file?.name || doc.label || 'document').slice(0, 80),
          fileSizeKB: Math.round((file?.size || 0) / 1024),
          fileType: file?.type || 'none',
          emptyFile: !file || file.size === 0,
        },
      });

      reportDiag(
        screeningId,
        'documents',
        'auto_upload_ok',
        { docType: doc.type, docSubtype: doc.subtype, fileSizeKB: Math.round((file?.size || 0) / 1024) },
        { email: emailFromQuery, name: nameFromQuery }
      );

      // Clear from selectedFiles so toUpload won't include it on submit
      setSelectedFiles((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      if (fileInputRefs.current[key]) {
        fileInputRefs.current[key].value = '';
      }

      // Refresh server state so uploadedKeys reflects this document
      await refreshRequestData();
      setReplacingKeys((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
      // No success message here on purpose: the card turns green and says "Uploaded"
      // in the same beat. A toast saying so as well was the same fact twice, in the one
      // place on a phone that covers the next thing to do.
    } catch (err) {
      // Non-fatal — handleSubmitAll will retry via handleUploadSingle if needed.
      // The file stays in selectedFiles so the submit path picks it up.
      console.warn(`Auto-upload failed for ${key}:`, err);
      reportDiag(
        screeningId,
        'documents',
        'auto_upload_failed',
        {
          docType: doc.type,
          docSubtype: doc.subtype,
          httpStatus: err?.response?.status ?? null,
          errorMessage: String(err?.message || '').slice(0, 120),
        },
        { email: emailFromQuery, name: nameFromQuery }
      );
      throw err; // re-throw so callers' catch blocks can log
    } finally {
      setUploadingKeys((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const handleFileChange = async (doc, file) => {
    const key = `${doc.type}::${doc.subtype}`;
    clearAlert();
    setDocStatuses((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    if (!file) {
      setSelectedFiles((prev) => ({ ...prev, [key]: null }));
      setVerificationResults((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      setBankConfirmations((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      setBankFields((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      return;
    }

    // Read the bytes now, before they are parked in state waiting for the
    // candidate to press Upload.
    const snap = await snapshotFile(file);

    if (snap.error) {
      setSelectedFiles((prev) => ({ ...prev, [key]: null }));
      setDocStatuses((prev) => ({
        ...prev,
        [key]: {
          code: DOC_PROBLEM.READ_FAILED,
          title: 'We could not open that file',
          message:
            "If it is in Google Drive, WhatsApp or another cloud app, open it there once so it downloads to this device, then choose it again.",
          fix: 'retake',
          actionLabel: 'Choose the file again',
          comparison: null,
          tips: [],
          helpNote: null,
          allowManualEntry: false,
        },
      }));
      reportDiag(
        screeningId,
        'documents',
        'file_read_error',
        {
          docType: doc.type,
          docSubtype: doc.subtype,
          fileName: String(file.name || '').slice(0, 80),
          fileSizeKB: Math.round((file.size || 0) / 1024),
          reason: snap.skippedReason || 'read_failed',
          bytesRead: snap.bytes,
          errorName: snap.error?.name,
          errorMessage: snap.error?.message,
        },
        { email: emailFromQuery, name: nameFromQuery }
      );
      return;
    }

    // Aadhaar and PAN used to be refused here unless they were images. That was backwards:
    // the only Aadhaar a candidate can obtain for themselves is the UIDAI e-Aadhaar, which
    // is a PDF, and a phone that "scans" a card writes one too. The server reads a PDF's
    // text layer when it has one and rasterises it when it does not, so the format the
    // candidate happens to have is no longer their problem to solve.

    // Phone photos are 3–9 MB and the stored copy never needs to be. Shrunk once, here,
    // so the OCR read, the preview, the upload and any retry all send the small file.
    const fitted = await fitImageForStorage(snap.file);
    const pickedFile = fitted.file;
    if (fitted.compressed) {
      reportDiag(
        screeningId,
        'documents',
        'image_compressed',
        {
          docType: doc.type,
          docSubtype: doc.subtype,
          originalKB: Math.round(fitted.originalBytes / 1024),
          compressedKB: Math.round(pickedFile.size / 1024),
          dimensions: `${fitted.width}x${fitted.height}`,
          quality: fitted.quality,
        },
        { email: emailFromQuery, name: nameFromQuery }
      );
    }

    setSelectedFiles((prev) => ({ ...prev, [key]: pickedFile }));
    rememberPreview(key, pickedFile);

    // If ID verification is enabled (or govt ID image is picked), run AI verification
    const isVerificationEnabled =
      docRequestData?.enableDocumentVerification !== false &&
      applyInfo?.enableDocumentVerification !== false;

    // Images and PDFs are both readable now, for government IDs as well as bank documents.
    const isVerifiableFile = !!pickedFile &&
      (pickedFile.type?.startsWith('image/') || pickedFile.type === 'application/pdf');

    if ((doc.type === 'govt_id' || isBankDoc(doc)) && isVerificationEnabled && isVerifiableFile) {
      setVerifyingKeys((prev) => {
        const next = new Set(prev);
        next.add(key);
        return next;
      });

      try {
        const fileToVerify = await compressImageForVerification(pickedFile);
        const vFd = new FormData();
        vFd.append('file', fileToVerify);
        vFd.append('type', doc.type);
        vFd.append('subtype', doc.subtype);
        vFd.append('label', doc.label || '');

        const vRes = await axios.post(`/api/documents/verify-id/${token}`, vFd);
        const vData = vRes.data;

        setVerificationResults((prev) => ({
          ...prev,
          [key]: vData,
        }));

        // One decision, one place. What this used to do with toasts — a reason in a box
        // that covered the corner of the screen and timed out while it was being read —
        // now flows through buildDocumentStatus into the card under the document, which
        // is also what the card renders from, so the two can no longer disagree.
        //
        // Bank documents are not classified, deliberately: a cancelled cheque, a passbook
        // page, a statement and a banking-app screenshot share no layout, so "does this
        // look like a bank document" has no answer. What the data says stands in for it —
        // an IFSC resolving to a real RBI branch could not have come from anything else.
        const problem = buildDocumentStatus({ doc, result: vData, candidateName: nameFromQuery });
        setDocStatuses((prev) => ({ ...prev, [key]: problem }));

        if (isBankDoc(doc) && (!problem || problem.allowManualEntry)) {
          // Fill in what was read so the candidate confirms rather than retypes — also
          // under a "could not read X" card, which is exactly when the rest helps. A field
          // the read did not produce starts empty, never pre-filled with a guess.
          const read = vData?.fields || {};
          const extAcc = read.accountNumber || vData?.bank?.accountNumber;
          setDocumentNumbers((prev) => ({ ...prev, [key]: extAcc ? accountDigits(extAcc) : '' }));
          setBankFields((prev) => ({
            ...prev,
            [key]: { ifsc: normalizeIfscInput(read.ifsc || vData?.bank?.ifsc), holderName: read.holderName || '', ifscLookup: null },
          }));
          setBankConfirmations((prev) => { const next = { ...prev }; delete next[key]; return next; });
          return;
        }

        if (problem) {
          // A problem the candidate cannot type their way around clears the file, so a
          // rejected document can never ride along to submit. A read failure keeps it:
          // the document may be fine and the manual field is the designed fallback.
          if (!problem.allowManualEntry) {
            setSelectedFiles((prev) => { const next = { ...prev }; delete next[key]; return next; });
            setDocumentNumbers((prev) => { const next = { ...prev }; delete next[key]; return next; });
            setBankConfirmations((prev) => { const next = { ...prev }; delete next[key]; return next; });
            setBankFields((prev) => { const next = { ...prev }; delete next[key]; return next; });
            if (fileInputRefs.current[key]) fileInputRefs.current[key].value = '';
          }
          return;
        }

        const extId = vData?.validation?.normalizedData?.id_number;
        setDocumentNumbers((prev) => ({ ...prev, [key]: extId }));

        if (doc.type === 'govt_id') {
          // Do not auto-upload for Govt IDs. Let the candidate confirm and edit fields.
          setOcrEditableFields((prev) => ({
            ...prev,
            [key]: {
              name: vData?.validation?.normalizedData?.name || vData?.validation?.rawExtractedFields?.Name || '',
              dob: vData?.validation?.normalizedData?.dob || '',
            }
          }));
        } else {
          // Auto-upload the moment OCR clears it for non-govt IDs.
          try {
            await autoUploadDoc(doc, pickedFile, extId, vData);
          } catch (_uploadErr) {
            // Non-fatal: the submit handler retries.
          }
        }
      } catch (vErr) {
        // A failure here means our OCR pipeline could not run — it says nothing
        // about the candidate's document. Staying silent left them staring at an
        // empty number field with no idea anything had gone wrong, so say so and
        // point at the manual field below, which is already the designed fallback.
        console.warn('ID verification error:', vErr);
        reportDiag(
          screeningId,
          'documents',
          'verify_id_failed',
          {
            docType: doc.type,
            docSubtype: doc.subtype,
            httpStatus: vErr?.response?.status ?? null,
            serverMessage: String(vErr?.response?.data?.error || vErr?.response?.data?.message || '').slice(0, 200),
            errorCode: vErr?.code || null,
            errorMessage: String(vErr?.message || '').slice(0, 120),
            isNetworkFailure: !vErr?.response,
          },
          { email: emailFromQuery, name: nameFromQuery }
        );
        // Built by buildDocumentStatus like every other outcome. A hand-rolled card here
        // is how the wording drifted from the one the module owns — and this module
        // exists because that divergence happened before.
        setDocStatuses((prev) => ({
          ...prev,
          [key]: buildDocumentStatus({
            doc,
            result: { success: false, errorReason: vErr?.response?.data?.errorReason || null },
            candidateName: nameFromQuery,
          }),
        }));
      } finally {
        setVerifyingKeys((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
      }
    } else {
      // ── Non-govt-id docs: auto-upload immediately after file selection ──
      // Resume, Passport-photo, etc. — no OCR needed, upload right away.
      // A bank document is excluded even with verification switched off: the server
      // requires the typed account number either way, so uploading before the candidate
      // has typed it would fail the request rather than save a step.
      // A government ID is excluded for the same reason: with verification off there is
      // no OCR number yet, the server rejects a govt_id without one ("Document number is
      // required", 400), and the whole photo was then sent again on submit.
      if (pickedFile && !isBankDoc(doc) && doc.type !== 'govt_id') {
        try {
          await autoUploadDoc(doc, pickedFile, '');
        } catch (_uploadErr) {
          // Non-fatal: the submit handler will retry if this fails
        }
      }
    }
  };

  const handleUploadSingle = async (doc) => {
    const key = `${doc.type}::${doc.subtype}`;
    const file = selectedFiles[key];
    const isTextOnly =
      ['uan_number', 'marital_status', 'complete_address'].includes(doc.subtype) ||
      isPanAckBranch(doc, key);
    if (!file && !isTextOnly) return;

    let docNum = '';
    if (doc.type === 'govt_id') {
      docNum = (documentNumbers[key] || '').trim();
    } else if (isBankDoc(doc)) {
      docNum = accountDigits(documentNumbers[key]);
    } else if (doc.subtype === 'uan_number') {
      docNum = (documentNumbers[key] || '').replace(/\s+/g, '');
    } else if (doc.subtype === 'marital_status' || doc.subtype === 'complete_address') {
      docNum = (documentNumbers[key] || '').trim();
    }

    setUploadingKeys((prev) => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });

    try {
      const fd = new FormData();
      if (file) {
        fd.append('file', file);
      }
      fd.append('type', doc.type);
      fd.append('subtype', doc.subtype);
      fd.append('label', doc.label);
      if (docNum) {
        fd.append('documentNumber', docNum);
      }
      if (isPanAckBranch(doc, key)) {
        fd.append('hasPan', 'false');
      }
      const vRes = verificationResults[key];
      if (isBankDoc(doc)) {
        appendBankFields(fd, key, vRes);
      } else if (vRes?.validation || vRes?.normalizedData) {
        fd.append('verificationDetails', JSON.stringify(govtIdPayload(key, vRes, docNum)));
      }
      fd.append('consentGiven', 'true');
      fd.append('consentIp', '');

      // No explicit Content-Type — the browser must set it so the multipart
      // boundary is included. See the resume upload for the full explanation.
      //
      // postWithDiag tracks upload progress so a failure is reported with the
      // bytes actually sent. Zero bytes in under ~200ms means the request never
      // left the browser (blocked/malformed) rather than a dropped connection —
      // the distinction that took weeks to establish on the resume step.
      await postWithDiag(`/api/documents/upload/${token}`, fd, {
        screeningId,
        stage: 'documents',
        event: 'upload',
        identity: { email: emailFromQuery, name: nameFromQuery },
        detail: {
          docType: doc.type,
          docSubtype: doc.subtype,
          fileName: String(file?.name || doc.label || 'document').slice(0, 80),
          fileSizeKB: Math.round((file?.size || 0) / 1024),
          fileType: file?.type || 'none',
          emptyFile: !file || file.size === 0,
        },
      });

      reportDiag(
        screeningId,
        'documents',
        'upload_ok',
        { docType: doc.type, docSubtype: doc.subtype, fileSizeKB: Math.round((file?.size || 0) / 1024) },
        { email: emailFromQuery, name: nameFromQuery }
      );

      setSelectedFiles((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      if (fileInputRefs.current[key]) {
        fileInputRefs.current[key].value = '';
      }
    } catch (err) {
      raiseAlert(
        `We could not upload your ${doc.label}. ${err?.response?.data?.message || err?.message || 'Please check your connection and try again.'}`
      );
      throw err;
    } finally {
      setUploadingKeys((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const hasAssessment = !!applyInfo?.hasAssessment;
  const hasInterview = !!applyInfo?.hasInterview;
  const isAssessmentPending = hasAssessment && !applyInfo?.assessmentCompleted && applyInfo?.assessmentSharingLink;
  const isInterviewPending = hasInterview && !applyInfo?.interviewCompleted && applyInfo?.interviewSharingLink;
  const hasNextStep = isAssessmentPending || isInterviewPending;

  // ── Multi-step navigation ──
  const goToNextDoc = useCallback(() => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentDocIndex((prev) => Math.min(prev + 1, requestedDocs.length - 1));
    setTimeout(() => setIsTransitioning(false), 350);
  }, [isTransitioning, requestedDocs.length]);

  const goToPrevDoc = useCallback(() => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentDocIndex((prev) => Math.max(prev - 1, 0));
    setTimeout(() => setIsTransitioning(false), 350);
  }, [isTransitioning]);

  const goToDocIndex = useCallback((idx) => {
    if (isTransitioning || idx === currentDocIndex) return;
    setIsTransitioning(true);
    setCurrentDocIndex(Math.max(0, Math.min(idx, requestedDocs.length - 1)));
    setTimeout(() => setIsTransitioning(false), 350);
  }, [isTransitioning, currentDocIndex, requestedDocs.length]);

  // Initialize to first non-uploaded doc on page load
  useEffect(() => {
    if (hasInitializedStepRef.current || requestedDocs.length === 0) return;
    const firstPending = requestedDocs.findIndex(
      (d) => !uploadedKeys.has(`${d.type}::${d.subtype}`)
    );
    if (firstPending > 0) setCurrentDocIndex(firstPending);
    hasInitializedStepRef.current = true;
  }, [requestedDocs, uploadedKeys]);

  // Auto-advance 1.2s after current doc gets uploaded
  useEffect(() => {
    if (requestedDocs.length === 0) return;
    const currentDoc = requestedDocs[currentDocIndex];
    if (!currentDoc) return;
    const currentKey = `${currentDoc.type}::${currentDoc.subtype}`;

    const wasUploaded = prevUploadedKeysRef.current.has(currentKey);
    const isNowUploaded = uploadedKeys.has(currentKey);
    prevUploadedKeysRef.current = new Set(uploadedKeys);

    if (!wasUploaded && isNowUploaded && currentDocIndex < requestedDocs.length - 1) {
      const timer = setTimeout(() => goToNextDoc(), 1200);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadedKeys, currentDocIndex, requestedDocs]);

  const isLastDocStep = currentDocIndex >= requestedDocs.length - 1;
  const isFirstDocStep = currentDocIndex === 0;

  const allDocsUploaded = requestedDocs.length > 0 && requestedDocs.every(
    (d) => uploadedKeys.has(`${d.type}::${d.subtype}`)
  );

  const handleNextStep = () => {
    // Cleared on every attempt so a stale complaint cannot outlive the thing it was
    // complaining about — and so raiseAlert re-announces when the same one recurs.
    clearAlert();
    const doc = requestedDocs[currentDocIndex];
    if (!doc) return;
    const key = `${doc.type}::${doc.subtype}`;

    if (uploadedKeys.has(key) && !replacingKeys.has(key)) { goToNextDoc(); return; }
    if (doc.required === false && !selectedFiles[key]) { goToNextDoc(); return; }

    if (doc.subtype === 'uan_number') {
      if (/^\d{12}$/.test((documentNumbers[key] || '').replace(/\s+/g, ''))) {
        goToNextDoc(); return;
      }
      raiseAlert('Please enter a valid 12-digit UAN number before continuing.');
      return;
    }
    // Must precede the govt_id file checks below — the "no PAN yet" branch has no file to wait on.
    if (isPanDeclarationDoc(doc)) {
      if (!panChoices[key]) {
        raiseAlert('Please select whether you have a PAN Card (Yes or No).');
        return;
      }
      if (panChoices[key] === 'no') {
        if (isValidPanAck(documentNumbers[key])) { goToNextDoc(); return; }
        raiseAlert('Please enter your PAN acknowledgement number before continuing.');
        return;
      }
    }
    if (doc.subtype === 'marital_status') {
      const val = documentNumbers[key];
      if (val === 'Single' || val === 'Married') { goToNextDoc(); return; }
      raiseAlert('Please select your marital status before continuing.');
      return;
    }
    if (doc.subtype === 'complete_address') {
      const val = documentNumbers[key];
      if (val && val.trim().length > 5) { goToNextDoc(); return; }
      raiseAlert('Please enter your complete address before continuing.');
      return;
    }

    if (!selectedFiles[key]) {
      raiseAlert(`Please upload your ${doc.label} before continuing.`);
      return;
    }
    if (verifyingKeys.has(key)) {
      raiseAlert('Please wait — we are still reading your document.');
      return;
    }
    if (uploadingKeys.has(key)) {
      raiseAlert('Please wait — your document is still uploading.');
      return;
    }
    if (doc.type === 'govt_id' && !documentNumbers[key]?.trim()) {
      raiseAlert(`Please confirm your ${doc.label} number before continuing.`);
      return;
    }
    if (isBankDoc(doc)) {
      if (!isValidAccountNumber(documentNumbers[key])) {
        raiseAlert(
          `Please enter a valid bank account number (${MIN_ACCOUNT_DIGITS}-${MAX_ACCOUNT_DIGITS} digits).`
        );
        return;
      }
      if (!isValidIfsc(bankFields[key]?.ifsc) || bankFields[key]?.ifscLookup?.valid === false) {
        raiseAlert('Please enter the IFSC code of your bank branch (11 characters, e.g. SBIN0001234).');
        return;
      }
      if (!isValidHolderName(bankFields[key]?.holderName)) {
        raiseAlert('Please enter the name on your bank account.');
        return;
      }
      if (!bankConfirmations[key]) {
        raiseAlert('Please check the confirmation box to confirm your bank details are correct.');
        return;
      }
    }

    // Edge case: file selected but auto-upload failed — trigger manual upload then advance
    handleUploadSingle(doc).then(() => {
      refreshRequestData().then(() => goToNextDoc());
    }).catch(() => {});
  };

  const handleSubmitAll = async (e) => {
    clearAlert();
    e.preventDefault();
    setHasAttemptedSubmit(true);

    if (verifyingKeys.size > 0) {
      raiseAlert('Please wait — we are still reading your document.');
      return;
    }

    // The button stays pressable with something unfinished, so a press can say what. Greyed
    // out as "1 pending" it said nothing, and a candidate whose bank form was one character
    // off re-uploaded the same passbook twenty times trying to make it go.
    if (hasUnmetRequired) {
      raiseAlert(`Not finished yet: ${unmetRequiredDocs.map((d) => d.label).join(', ')}. Check the fields marked in red.`);
      return;
    }

    setSubmittingAll(true);
    const submitStartedAt = Date.now();
    reportDiag(
      screeningId,
      'documents',
      'submit_start',
      {
        pendingUploads: toUpload.length,
        totalPendingKB: Math.round(
          toUpload.reduce((sum, d) => sum + (selectedFiles[`${d.type}::${d.subtype}`]?.size || 0), 0) / 1024
        ),
        secondsLeftOnTimer: timeLeftSeconds,
      },
      { email: emailFromQuery, name: nameFromQuery }
    );
    try {
      if (toUpload.length > 0) {
        // 1. Upload all selected pending documents sequentially
        for (const doc of toUpload) {
          await handleUploadSingle(doc);
        }

        // 2. Refresh request status to confirm DB shows everything complete
        await refreshRequestData();
      }

      // 3. Finalize the step. The server enforces required documents and returns
      // REQUIRED_DOCUMENTS_MISSING if any are absent.
      try {
        await axios.post(`/api/documents/upload/${token}/complete`);
        reportDiag(
          screeningId,
          'documents',
          'submit_ok',
          { uploaded: toUpload.length, totalDurationMs: Date.now() - submitStartedAt },
          { email: emailFromQuery, name: nameFromQuery }
        );
      } catch (completeErr) {
        console.error(completeErr);
        const code = completeErr?.response?.data?.code;
        reportDiag(
          screeningId,
          'documents',
          'complete_call_failed',
          {
            httpStatus: completeErr?.response?.status ?? null,
            serverCode: code || null,
            errorCode: completeErr?.code || null,
            errorMessage: String(completeErr?.message || '').slice(0, 120),
            isNetworkFailure: !completeErr?.response,
            totalDurationMs: Date.now() - submitStartedAt,
          },
          { email: emailFromQuery, name: nameFromQuery }
        );

        // Required documents are missing — the step is genuinely not complete, so
        // do NOT advance. Falling through here would walk the candidate into the
        // assessment without the documents that gate it, which is exactly what
        // this enforcement exists to prevent.
        if (code === 'REQUIRED_DOCUMENTS_MISSING') {
          await refreshRequestData();
          raiseAlert(
            completeErr?.response?.data?.message ||
            'Please upload all required documents before continuing.'
          );
          return;
        }

        // Any other failure (network, server error): the step may well have been
        // recorded, so continue rather than trapping the candidate — but it has
        // been reported above rather than swallowed.
      }

      // 4. If Assessment or Interview is pending, auto-redirect to it to minimize clicks.
      const urlParams = new URLSearchParams();
      if (emailFromQuery.trim()) urlParams.set('email', emailFromQuery.trim());
      if (nameFromQuery.trim()) urlParams.set('name', nameFromQuery.trim());

      const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
      const hubQuery = urlParams.toString();
      const hubReturnUrl = `${baseUrl}/apply/${screeningId}/hub${hubQuery ? `?${hubQuery}` : ''}`;

      // The Application Form comes straight after documents, before the assessment.
      if (applyInfo?.applicationFormToken && !applyInfo?.applicationFormCompleted) {
        navigate(
          applicationFormPath(screeningId, applyInfo.applicationFormToken, {
            email: emailFromQuery,
            name: nameFromQuery,
          }),
          { replace: true }
        );
      } else if (isAssessmentPending) {
        let assessmentReturnUrl = hubReturnUrl;
        if (isInterviewPending) {
          const iQuery = new URLSearchParams(urlParams);
          iQuery.set('returnUrl', hubReturnUrl);
          iQuery.set('screeningId', screeningId);
          iQuery.set('hasAssessment', 'true');
          assessmentReturnUrl = `${baseUrl}/ai-interview/${applyInfo.interviewSharingLink}?${iQuery.toString()}`;
        }

        const aQuery = new URLSearchParams(urlParams);
        aQuery.set('returnUrl', assessmentReturnUrl);
        aQuery.set('screeningId', screeningId);
        if (hasInterview) {
          aQuery.set('hasInterview', 'true');
          if (applyInfo.interviewSharingLink) {
            aQuery.set('interviewSharingLink', applyInfo.interviewSharingLink);
          }
        }

        navigate(`/assessment/${applyInfo.assessmentSharingLink}?${aQuery.toString()}`, { replace: true });
      } else if (isInterviewPending) {
        const iQuery = new URLSearchParams(urlParams);
        iQuery.set('returnUrl', hubReturnUrl);
        iQuery.set('screeningId', screeningId);
        if (hasAssessment) iQuery.set('hasAssessment', 'true');

        navigate(`/ai-interview/${applyInfo.interviewSharingLink}?${iQuery.toString()}`, { replace: true });
      } else {
        navigate(`/apply/${screeningId}/thank-you?${urlParams.toString()}`, { replace: true });
      }
    } catch (err) {
      console.error(err);
      // The per-upload failure has already been reported with byte counts by
      // postWithDiag; this records that the whole step aborted, so the log shows
      // the candidate stopped here rather than simply going quiet.
      reportDiag(
        screeningId,
        'documents',
        'submit_aborted',
        {
          pendingUploads: toUpload.length,
          httpStatus: err?.response?.status ?? null,
          errorCode: err?.code || null,
          errorMessage: String(err?.message || '').slice(0, 120),
          isNetworkFailure: !err?.response,
          totalDurationMs: Date.now() - submitStartedAt,
        },
        { email: emailFromQuery, name: nameFromQuery }
      );
      // Previously the candidate saw nothing at all here — the spinner just
      // stopped. Tell them, so they retry instead of assuming it worked.
      raiseAlert('We could not finish submitting your documents. Please try again.');
    } finally {
      setSubmittingAll(false);
    }
  };

  // Stepper calculations
  const resumeEnabled = applyInfo?.hasResumeScoring !== false;
  const step2Label = resumeEnabled ? 'Resume upload' : 'Additional details';
  const steps = [
    'Basic details',
    step2Label,
    'Document upload',
    ...(applyInfo?.includeApplicationForm ? ['Application form'] : []),
    ...(applyInfo?.hasAssessment ? ['Assessment'] : []),
    ...(applyInfo?.hasInterview ? ['AI interview'] : []),
  ];
  const currentStepIndex = 2;
  const interviewType = applyInfo?.interviewType || 'voice';
  const STEP_META = getStepMeta(interviewType);

  if (loading) {
    return (
      <div className="min-h-screen bg-white-smoke flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-electric-blue" />
          <p className="text-sm text-gray-500">Loading secure portal…</p>
        </div>
      </div>
    );
  }

  if (errorMsg) {
    return (
      <div className="min-h-screen bg-white-smoke flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-lg rounded-2xl border border-gray-200 bg-white p-8 shadow-sm text-center">
          <h1 className="text-lg font-semibold text-gray-900">Secure link inactive</h1>
          <p className="mt-3 text-sm leading-relaxed text-gray-600">{errorMsg}</p>
          <p className="mt-6 text-xs text-gray-400 border-t border-gray-100 pt-4">
            TalentCrux — Secure BGV Document Collection Portal.
          </p>
        </div>
      </div>
    );
  }

  const companyName = docRequestData?.companyName || 'the recruiting organization';
  const jobTitle = docRequestData?.jobTitle || 'the role';

  return (
    <div className="min-h-screen bg-white-smoke px-3 sm:px-6 py-3 sm:py-6 md:py-8">
      <DocumentPreviewModal
        open={!!preview?.open}
        onClose={() => setPreview(null)}
        source={preview?.source}
        fileName={preview?.fileName}
        loading={!!preview?.loading}
        error={preview?.error}
      />

      <LeaveScreeningModal
        isOpen={showLeaveModal}
        onClose={() => setShowLeaveModal(false)}
        onStay={() => setShowLeaveModal(false)}
        onLeave={() => {
          setShowLeaveModal(false);
          navigate(-1);
        }}
        title="Leave this page?"
        message={
          requestedDocs.some(doc => doc.required !== false)
            ? "Your progress will be saved, but you must complete all mandatory document uploads to finish your application."
            : "Your progress will not be lost. This document upload step is optional — you can also skip it and finish your application."
        }
      />

      <div className="mx-auto max-w-5xl">
        {/* Header */}
        <header className="flex items-center justify-between gap-3">
          <div className="flex flex-col min-w-0">
            <p className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-wide text-gray-400">
              Powered by
            </p>
            <div className="mt-0.5 flex items-center gap-1.5">
              <img
                src="/logo.svg"
                alt="TalentCrux"
                className="h-8 w-8 sm:h-12 sm:w-12 md:h-14 md:w-14 object-contain flex-shrink-0"
              />
              <span className="text-base sm:text-xl md:text-2xl font-bold text-gray-900 truncate">
                TalentCrux
              </span>
            </div>
          </div>
        </header>

        {/* Stepper progress */}
        {steps.length > 0 && (
          <section className="mt-4 sm:mt-8">
            <p className="text-center text-[10px] sm:text-xs font-semibold uppercase tracking-[0.2em] text-gray-500 mb-4 sm:mb-6">
              Your screening journey
            </p>
            <div className="flex items-start justify-center gap-0 overflow-x-auto py-1">
              {steps.map((label, index) => {
                const meta = STEP_META[label] || {
                  icon: CheckIcon,
                  desc: STEP_SECONDARY[label] || '',
                  gradient: 'from-gray-400 to-gray-500',
                };
                const StepIcon = meta.icon;
                const isActive = index === currentStepIndex;
                const isCompleted = index < currentStepIndex;
                return (
                  <React.Fragment key={label}>
                    <div className="flex flex-col items-center text-center min-w-0 flex-1 sm:flex-none sm:w-32">
                      <span
                        className={`text-[9px] sm:text-xs font-semibold uppercase tracking-[0.06em] sm:tracking-[0.14em] whitespace-nowrap mb-1 ${isActive
                            ? 'text-electric-blue font-bold'
                            : isCompleted
                              ? 'text-emerald-500'
                              : 'text-gray-400'
                          }`}
                      >
                        Step {index + 1}
                      </span>
                      <div
                        className={`relative flex h-11 w-11 sm:h-16 sm:w-16 items-center justify-center rounded-xl sm:rounded-2xl transition-all duration-500 ${isActive
                            ? `bg-gradient-to-br ${meta.gradient} text-white shadow-md sm:shadow-lg`
                            : isCompleted
                              ? 'bg-emerald-50 text-emerald-500 border-2 border-emerald-200'
                              : 'bg-white text-gray-300 border-2 border-gray-200 border-dashed'
                          }`}
                        style={
                          isActive
                            ? { boxShadow: '0 6px 20px rgba(0, 123, 255, 0.25)' }
                            : isCompleted
                              ? { boxShadow: '0 4px 14px rgba(16, 185, 129, 0.15)' }
                              : {}
                        }
                      >
                        {isCompleted ? (
                          <CheckIcon className="h-4 w-4 sm:h-6 sm:w-6" strokeWidth={2.5} />
                        ) : (
                          <StepIcon
                            className="h-4 w-4 sm:h-7 sm:w-7"
                            strokeWidth={isActive ? 2 : 1.5}
                          />
                        )}
                        {isActive && (
                          <span
                            className="absolute inset-0 rounded-xl sm:rounded-2xl border-2 border-electric-blue/30 animate-ping"
                            style={{ animationDuration: '2s' }}
                          />
                        )}
                      </div>
                      <p
                        className={`mt-1.5 text-[10px] sm:text-sm font-semibold leading-tight hyphens-auto [overflow-wrap:anywhere] ${isActive
                            ? 'text-gray-900 font-bold'
                            : isCompleted
                              ? 'text-emerald-700'
                              : 'text-gray-400'
                          }`}
                      >
                        {label === 'AI interview' ? getInterviewStepLabel(interviewType) : label}
                      </p>
                    </div>
                    {index < steps.length - 1 && (
                      <div className="flex-1 flex items-center pt-[36px] sm:pt-[52px] min-w-[6px] max-w-[12px] sm:max-w-[72px]">
                        <div className="w-full flex items-center gap-0.5">
                          {[...Array(3)].map((_, i) => (
                            <div
                              key={i}
                              className={`flex-1 h-0.5 rounded-full ${index < currentStepIndex ? 'bg-emerald-400/70' : 'bg-gray-200'
                                }`}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </section>
        )}

        {/* Main Content card */}
        <div className="mt-4 sm:mt-8">
          <div className="bg-white rounded-2xl sm:rounded-3xl border border-gray-200 shadow-sm sm:shadow-md overflow-hidden">
            {/* Header strip */}
            <div className="bg-[#007BFF] px-4 py-4 sm:px-6 sm:py-5">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="text-base sm:text-xl lg:text-2xl font-bold text-white">
                    Submit Documents {!hasAnyRequiredDocs && <span className="text-xs sm:text-sm font-semibold text-white/80">(optional)</span>}
                  </h2>
                  <p className="text-xs sm:text-sm text-white/90 mt-0.5">
                    Requested by <span className="font-semibold">{companyName}</span> for the position of <span className="font-semibold">{jobTitle}</span>.
                  </p>
                </div>
                <div className="self-start sm:self-auto inline-flex items-center gap-1.5 rounded-full bg-white/95 px-3 py-1 text-[11px] sm:text-xs font-semibold text-blue-800 shadow-2xs shrink-0 whitespace-nowrap">
                  <ClockIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-blue-600" />
                  <span>
                    {isExpired ? (
                      <span className="text-red-600">Time up - window closed</span>
                    ) : (
                      <>
                        Upload within {Math.floor(timeLeftSeconds / 60).toString().padStart(2, '0')}:
                        {(timeLeftSeconds % 60).toString().padStart(2, '0')}
                      </>
                    )}
                  </span>
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="p-3.5 sm:p-6 lg:p-8">
              <form onSubmit={handleSubmitAll} className="space-y-6">
                {/* Step progress indicator */}
                {requestedDocs.length > 1 && (
                  <div className="mb-5 sm:mb-6">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-xs sm:text-sm font-bold text-gray-900">
                          Step {currentDocIndex + 1} of {requestedDocs.length}
                        </span>
                        <span className="text-xs text-gray-400 font-medium hidden sm:inline">
                          · {requestedDocs[currentDocIndex]?.label}
                        </span>
                      </div>
                      <span className="text-[10px] sm:text-xs font-semibold text-gray-500">
                        {uploadedKeys.size}/{requestedDocs.length} completed
                      </span>
                    </div>
                    {/* Progress bar */}
                    <div className="relative h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="absolute inset-y-0 left-0 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full transition-all duration-500 ease-out"
                        style={{ width: `${((uploadedKeys.size) / requestedDocs.length) * 100}%` }}
                      />
                    </div>
                    {/* Dot navigation */}
                    <div className="flex items-center justify-center gap-1.5 sm:gap-2 mt-3">
                      {requestedDocs.map((d, i) => {
                        const dk = `${d.type}::${d.subtype}`;
                        const isDone = uploadedKeys.has(dk);
                        const isCurrent = i === currentDocIndex;
                        return (
                          <button
                            key={i}
                            type="button"
                            onClick={() => goToDocIndex(i)}
                            className={`rounded-full transition-all duration-300 cursor-pointer focus:outline-none ${
                              isCurrent
                                ? 'w-7 sm:w-8 h-2.5 bg-gradient-to-r from-blue-500 to-indigo-500 shadow-sm'
                                : isDone
                                  ? 'w-2.5 h-2.5 bg-emerald-400 hover:bg-emerald-500'
                                  : 'w-2.5 h-2.5 bg-gray-200 hover:bg-gray-300'
                            }`}
                            aria-label={`Go to document ${i + 1}: ${d.label}`}
                          />
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 sm:gap-8">
                  {/* Left: Document step card */}
                  <div className="lg:col-span-8">
                    {(() => {
                      const doc = requestedDocs[currentDocIndex];
                      if (!doc) return null;
                      const idx = currentDocIndex;
                      const key = `${doc.type}::${doc.subtype}`;
                      // A document the candidate has chosen to redo stops counting as
                      // uploaded until the replacement lands. The old submission is still
                      // on the server — the server keeps every one and reads the newest —
                      // but the step has to show the controls again, and every panel here
                      // is gated on this one flag.
                      const isReplacing = replacingKeys.has(key);
                      const isUploaded = uploadedKeys.has(key) && !isReplacing;
                      const fileSelected = selectedFiles[key];

                      return (
                        <div
                          key={currentDocIndex}
                          style={{ animation: 'docStepIn 250ms ease-out' }}
                        >
                        <style>{`@keyframes docStepIn { from { opacity: 0; transform: translateX(24px); } to { opacity: 1; transform: translateX(0); } }`}</style>
                        <div
                          className={`p-3.5 sm:p-5 rounded-2xl border transition-all duration-200 bg-white ${isUploaded
                              ? 'border-emerald-200 bg-emerald-50/20 shadow-2xs'
                              : fileSelected
                                ? 'border-blue-200 bg-blue-50/10 shadow-2xs'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                        >
                            {/*
                              The file picker for this document, mounted once and for as
                              long as the card is. It used to be nested inside whichever
                              branch was rendering a "Choose File" button, so it vanished
                              the moment a file was chosen — taking fileInputRefs with it,
                              and leaving every retry button clicking a detached node.
                              The visible controls below drive it by htmlFor.
                            */}
                            {isFileBackedDoc(doc) && (
                              <input
                                id={`file-input-${key}`}
                                type="file"
                                ref={(el) => (fileInputRefs.current[key] = el)}
                                accept=".pdf,.jpg,.jpeg,.png"
                                data-testid="document-upload-input"
                                onChange={(e) => handleFileChange(doc, e.target.files?.[0])}
                                className="sr-only"
                                disabled={isExpired}
                              />
                            )}

                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                              <div className="min-w-0 flex-1 space-y-1">
                                <div className="flex items-center flex-wrap gap-1.5 sm:gap-2">
                                  <DocumentTextIcon className={`h-4 w-4 sm:h-5 sm:w-5 shrink-0 ${isUploaded ? 'text-emerald-500' : 'text-[#007BFF]'}`} />
                                  <span className="text-sm sm:text-base font-bold text-gray-900">{doc.label}</span>
                                  {doc.required !== false ? (
                                    <span className="text-[9px] sm:text-[10px] font-bold text-red-600 bg-red-50 border border-red-100 px-1.5 py-0.5 rounded shrink-0">REQUIRED</span>
                                  ) : (
                                    <span className="text-[9px] sm:text-[10px] font-medium text-gray-500 bg-gray-100 border border-gray-200 px-1.5 py-0.5 rounded shrink-0">OPTIONAL</span>
                                  )}
                                  {isUploaded && (
                                    <span className="inline-flex items-center gap-1 text-[10px] sm:text-[11px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200/60">
                                      <CheckIcon className="h-3 w-3" strokeWidth={3} />
                                      Uploaded
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs text-gray-500 leading-relaxed sm:pl-7">
                                  {getUploadHint(doc)}
                                </p>
                              </div>

                              <div className="w-full sm:w-auto shrink-0 flex items-center justify-start sm:justify-end gap-2 pt-1 sm:pt-0 border-t border-gray-100 sm:border-t-0">
                                {isUploaded ? (
                                  /*
                                    Accepted is not the end of the candidate's interest in
                                    a document. They uploaded it from a phone gallery of
                                    near-identical photos, and the only way to know they
                                    sent the right one is to look. So an accepted document
                                    keeps both doors open: see it, or do it again.
                                  */
                                  <div className="flex items-center gap-1.5 w-full sm:w-auto">
                                    <span className="text-xs text-emerald-600 font-semibold flex items-center gap-1 mr-auto sm:mr-1">
                                      <CheckCircleIcon className="h-4 w-4 text-emerald-500" />
                                      Ready
                                    </span>
                                    {isFileBackedDoc(doc) && (
                                      <>
                                    {hasPreviewableFile(doc) && (
                                      <button
                                        type="button"
                                        onClick={() => openPreview(doc)}
                                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 active:bg-gray-100 text-[11px] font-bold text-gray-700 transition cursor-pointer shrink-0"
                                      >
                                        <EyeIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                                        View
                                      </button>
                                    )}
                                    <button
                                      type="button"
                                      onClick={() => replaceUploadedDoc(doc)}
                                      disabled={isExpired}
                                      className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 active:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed text-[11px] font-bold text-gray-700 transition cursor-pointer shrink-0"
                                    >
                                      <ArrowPathIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                                      Replace
                                    </button>
                                      </>
                                    )}
                                  </div>
                                ) : isPanDeclarationDoc(doc) ? (
                                  // Status only — the upload control for this document lives inside the
                                  // declaration card below, so that answering "Yes" visibly reveals it
                                  // instead of enabling a button sitting above the question.
                                  panChoices[key] === 'no' && isValidPanAck(documentNumbers[key]) ? (
                                    <span className="inline-flex items-center gap-1 text-[11px] font-bold text-blue-700 bg-blue-50 px-3 py-1.5 rounded-xl border border-blue-200 w-full sm:w-auto justify-center">
                                      <CheckIcon className="h-3.5 w-3.5 stroke-[2.5]" />
                                      Acknowledgement Entered
                                    </span>
                                  ) : panChoices[key] === 'yes' && fileSelected ? (
                                    <span className="inline-flex items-center gap-1 text-[11px] font-bold text-blue-700 bg-blue-50 px-3 py-1.5 rounded-xl border border-blue-200 w-full sm:w-auto justify-center">
                                      <CheckIcon className="h-3.5 w-3.5 stroke-[2.5]" />
                                      File Selected
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-700 bg-amber-50 px-3 py-1.5 rounded-xl border border-amber-200 w-full sm:w-auto justify-center">
                                      Input Required
                                    </span>
                                  )
                                ) : doc.subtype === 'uan_number' ? (
                                  /^\d{12}$/.test((documentNumbers[key] || '').replace(/\s+/g, '')) ? (
                                    <span className="inline-flex items-center gap-1 text-[11px] font-bold text-blue-700 bg-blue-50 px-3 py-1.5 rounded-xl border border-blue-200 w-full sm:w-auto justify-center">
                                      <CheckIcon className="h-3.5 w-3.5 stroke-[2.5]" />
                                      UAN Entered
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-700 bg-amber-50 px-3 py-1.5 rounded-xl border border-amber-200 w-full sm:w-auto justify-center">
                                      Input Required
                                    </span>
                                  )
                                ) : doc.subtype === 'marital_status' ? (
                                  (documentNumbers[key] === 'Single' || documentNumbers[key] === 'Married') ? (
                                    <span className="inline-flex items-center gap-1 text-[11px] font-bold text-blue-700 bg-blue-50 px-3 py-1.5 rounded-xl border border-blue-200 w-full sm:w-auto justify-center">
                                      <CheckIcon className="h-3.5 w-3.5 stroke-[2.5]" />
                                      Recorded
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-700 bg-amber-50 px-3 py-1.5 rounded-xl border border-amber-200 w-full sm:w-auto justify-center">
                                      Input Required
                                    </span>
                                  )
                                ) : doc.subtype === 'complete_address' ? (
                                  documentNumbers[key]?.trim()?.length > 5 ? (
                                    <span className="inline-flex items-center gap-1 text-[11px] font-bold text-blue-700 bg-blue-50 px-3 py-1.5 rounded-xl border border-blue-200 w-full sm:w-auto justify-center">
                                      <CheckIcon className="h-3.5 w-3.5 stroke-[2.5]" />
                                      Recorded
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-700 bg-amber-50 px-3 py-1.5 rounded-xl border border-amber-200 w-full sm:w-auto justify-center">
                                      Input Required
                                    </span>
                                  )
                                ) : fileSelected ? (
                                  <div className="flex items-center justify-between gap-2 bg-slate-100 border border-gray-200 px-3 py-2 sm:py-1.5 rounded-xl text-xs w-full sm:w-auto max-w-full sm:max-w-[240px]">
                                    <span className="truncate text-gray-700 font-medium">{fileSelected.name}</span>
                                    <button
                                      type="button"
                                      onClick={() => handleFileChange(doc, null)}
                                      className="p-1 text-gray-400 hover:text-red-500 rounded-lg hover:bg-gray-200 transition cursor-pointer shrink-0"
                                      aria-label="Clear selected file"
                                    >
                                      <XMarkIcon className="h-4 w-4" />
                                    </button>
                                  </div>
                                ) : (
                                  <label htmlFor={`file-input-${key}`} className={`w-full sm:w-auto flex items-center justify-center gap-1.5 px-4 py-2.5 sm:py-2 bg-white hover:bg-gray-50 active:bg-gray-100 border border-gray-300 hover:border-gray-400 rounded-xl cursor-pointer text-xs font-semibold text-gray-700 transition shadow-2xs ${isExpired ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                    <ArrowUpTrayIcon className="h-4 w-4 text-gray-500 shrink-0" />
                                    Choose File
                                  </label>
                                )}
                              </div>
                            </div>

                            {/* Replacing an accepted document — and the way back out of it. */}
                            {isReplacing && !fileSelected && (
                              <div className="mt-3 sm:pl-7 flex flex-col sm:flex-row sm:items-center gap-2 rounded-2xl border border-blue-200 bg-blue-50/70 px-3.5 py-2.5">
                                <p className="text-[11px] text-blue-900 leading-snug flex-1">
                                  Choose a new file to replace the {doc.label} you already uploaded. The one on
                                  file stays until the new one is accepted.
                                </p>
                                <button
                                  type="button"
                                  onClick={() => cancelReplace(doc)}
                                  className="shrink-0 text-[11px] font-bold text-blue-800 underline underline-offset-2 hover:text-blue-900 cursor-pointer text-left sm:text-center"
                                >
                                  Keep the one I uploaded
                                </button>
                              </div>
                            )}

                            {/* PAN declaration docs are excluded outright: the card below owns every
                                message for that step, so this generic one would double up. */}
                            {hasAttemptedSubmit && doc.required !== false && !isUploaded && !fileSelected && !['uan_number', 'marital_status', 'complete_address'].includes(doc.subtype) && !isPanDeclarationDoc(doc) && (
                              <p className="mt-2 text-[11px] font-bold text-red-500 sm:pl-7">
                                Please select a file for this mandatory document.
                              </p>
                            )}

                            {/* Specialized PAN Declaration Section (per-org — see panAckEnabled) */}
                            {isPanDeclarationDoc(doc) && !isUploaded && (
                              <div className="mt-3 sm:mt-4 sm:pl-7 space-y-3">
                                <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-3.5 sm:p-4 space-y-3 shadow-2xs">
                                  <div className="space-y-1">
                                    <label className="block text-xs font-bold text-slate-800">
                                      Do you have a PAN Card? <span className="text-red-500">*</span>
                                    </label>
                                    <p className="text-[11px] text-slate-500 leading-snug">
                                      If your PAN application is still in process, select "No" and enter the acknowledgement number from your application receipt.
                                    </p>
                                  </div>

                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setPanChoices((prev) => ({ ...prev, [key]: 'yes' }));
                                        setDocumentNumbers((prev) => ({ ...prev, [key]: '' }));
                                      }}
                                      className={`w-full py-2.5 px-3 rounded-xl text-xs font-bold transition border cursor-pointer text-center ${panChoices[key] === 'yes'
                                          ? 'bg-blue-600 border-blue-600 text-white shadow-xs'
                                          : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50 active:bg-slate-100'
                                        }`}
                                    >
                                      Yes, I have a PAN Card
                                    </button>

                                    <button
                                      type="button"
                                      onClick={() => {
                                        setPanChoices((prev) => ({ ...prev, [key]: 'no' }));
                                        // Clear any half-finished upload from the "Yes" path so a
                                        // stale file or OCR result cannot ride along with the
                                        // acknowledgement declaration.
                                        setDocumentNumbers((prev) => ({ ...prev, [key]: '' }));
                                        setSelectedFiles((prev) => {
                                          const next = { ...prev };
                                          delete next[key];
                                          return next;
                                        });
                                        setVerificationResults((prev) => {
                                          const next = { ...prev };
                                          delete next[key];
                                          return next;
                                        });
                                        if (fileInputRefs.current[key]) {
                                          fileInputRefs.current[key].value = '';
                                        }
                                      }}
                                      className={`w-full py-2.5 px-3 rounded-xl text-xs font-bold transition border cursor-pointer text-center ${panChoices[key] === 'no'
                                          ? 'bg-slate-800 border-slate-800 text-white shadow-xs'
                                          : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50 active:bg-slate-100'
                                        }`}
                                    >
                                      No, PAN applied / not yet received
                                    </button>
                                  </div>

                                  {/*
                                    The file picker is rendered here, under the question, rather than in
                                    the card header: answering "Yes" must visibly produce the next thing
                                    to do. The OCR states and the PAN-number field follow below this card,
                                    so the step reads straight down.
                                  */}
                                  {panChoices[key] === 'yes' && (
                                    <div className="space-y-2 pt-2 border-t border-slate-200/80 max-w-md">
                                      <label className="block text-xs font-bold text-gray-700">
                                        Upload your PAN Card photo <span className="text-red-500">*</span>
                                      </label>
                                      <p className="text-[11px] text-slate-500 leading-snug">
                                        Photo (JPEG/PNG) or PDF. We will read the PAN number from it — you can correct it below.
                                      </p>
                                      {fileSelected ? (
                                        <div className="flex items-center justify-between gap-2 bg-white border border-gray-200 px-3 py-2 rounded-xl text-xs">
                                          <span className="truncate text-gray-700 font-medium">{fileSelected.name}</span>
                                          <button
                                            type="button"
                                            onClick={() => handleFileChange(doc, null)}
                                            className="p-1 text-gray-400 hover:text-red-500 rounded-lg hover:bg-gray-100 transition cursor-pointer shrink-0"
                                            aria-label="Clear selected file"
                                          >
                                            <XMarkIcon className="h-4 w-4" />
                                          </button>
                                        </div>
                                      ) : (
                                        <label htmlFor={`file-input-${key}`} className={`w-full flex items-center justify-center gap-1.5 px-4 py-2.5 bg-white hover:bg-gray-50 active:bg-gray-100 border border-gray-300 hover:border-gray-400 rounded-xl cursor-pointer text-xs font-semibold text-gray-700 transition shadow-2xs ${isExpired ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                          <ArrowUpTrayIcon className="h-4 w-4 text-gray-500 shrink-0" />
                                          Choose PAN Card Photo
                                        </label>
                                      )}
                                      {hasAttemptedSubmit && !fileSelected && (
                                        <p className="text-[11px] font-bold text-red-500">
                                          Please upload a photo of your PAN Card.
                                        </p>
                                      )}
                                    </div>
                                  )}

                                  {panChoices[key] === 'no' && (
                                    <div className="space-y-2 pt-2 border-t border-slate-200/80 max-w-md">
                                      <label className="block text-xs font-bold text-gray-700">
                                        PAN Acknowledgement Number <span className="text-red-500">*</span>
                                      </label>
                                      <p className="text-[11px] text-slate-500 leading-snug">
                                        Enter the acknowledgement / coupon number printed on your PAN application receipt (Protean-NSDL or UTIITSL).
                                      </p>
                                      <input
                                        type="text"
                                        placeholder="e.g. 127712712121211"
                                        maxLength={20}
                                        value={documentNumbers[key] || ''}
                                        onChange={(e) => {
                                          const val = e.target.value.replace(/[^a-zA-Z0-9\s-]/g, '').slice(0, 20);
                                          setDocumentNumbers((prev) => ({ ...prev, [key]: val }));
                                        }}
                                        onBlur={() => {
                                          // Uploaded on blur, not per keystroke: unlike the fixed-width
                                          // UAN there is no length that reliably means "done typing".
                                          if (isValidPanAck(documentNumbers[key]) && !uploadingKeys.has(key)) {
                                            autoUploadDoc(doc, null, String(documentNumbers[key]).replace(/[\s-]/g, '').toUpperCase()).catch(() => { });
                                          }
                                        }}
                                        disabled={isExpired}
                                        className={`block w-full rounded-xl border ${hasAttemptedSubmit && !isValidPanAck(documentNumbers[key])
                                            ? 'border-red-400 focus:border-red-500 focus:ring-red-500/20'
                                            : 'border-gray-200 focus:border-electric-blue focus:ring-electric-blue/20'
                                          } px-3.5 py-3 sm:py-2.5 text-base sm:text-sm font-mono focus:outline-none focus:ring-2 bg-white shadow-2xs transition text-gray-900`}
                                      />
                                      {hasAttemptedSubmit && !isValidPanAck(documentNumbers[key]) && (
                                        <p className="text-[11px] font-bold text-red-500">
                                          Please enter the acknowledgement number from your PAN application receipt.
                                        </p>
                                      )}
                                    </div>
                                  )}

                                  {hasAttemptedSubmit && !panChoices[key] && (
                                    <p className="text-[11px] font-bold text-red-500 pt-1">
                                      Please select whether you have a PAN Card (Yes or No).
                                    </p>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Specialized UAN Section */}
                            {doc.subtype === 'uan_number' && !isUploaded && (
                              <div className="mt-3 sm:mt-4 sm:pl-7 space-y-3">
                                <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-3.5 sm:p-4 space-y-3 shadow-2xs">
                                  <div className="space-y-1">
                                    <label className="block text-xs font-bold text-slate-800">
                                      Enter 12-Digit UAN Number <span className="text-red-500">*</span>
                                    </label>
                                    <p className="text-[11px] text-slate-500 leading-snug">
                                      UAN is a 12-digit number allotted by EPFO to employees who have previously contributed to Provident Fund (PF).
                                    </p>
                                  </div>

                                  <div className="space-y-2 max-w-md">
                                    <input
                                      type="text"
                                      inputMode="numeric"
                                      placeholder="e.g. 100123456789"
                                      maxLength={12}
                                      value={documentNumbers[key] === 'NOT_APPLICABLE' ? '' : (documentNumbers[key] || '')}
                                      onChange={(e) => {
                                        const val = e.target.value.replace(/\D/g, '').slice(0, 12);
                                        setDocumentNumbers((prev) => ({ ...prev, [key]: val }));
                                        if (val.length === 12) {
                                          autoUploadDoc(doc, null, val).catch(() => { });
                                        }
                                      }}
                                      disabled={isExpired}
                                      className={`block w-full rounded-xl border ${hasAttemptedSubmit && (!documentNumbers[key]?.trim() || !/^\d{12}$/.test(documentNumbers[key]))
                                          ? 'border-red-400 focus:border-red-500 focus:ring-red-500/20'
                                          : 'border-gray-200 focus:border-electric-blue focus:ring-electric-blue/20'
                                        } px-3.5 py-3 sm:py-2.5 text-base sm:text-sm font-mono focus:outline-none focus:ring-2 bg-white shadow-2xs transition text-gray-900`}
                                    />
                                    {hasAttemptedSubmit && (!documentNumbers[key]?.trim() || !/^\d{12}$/.test(documentNumbers[key])) && (
                                      <p className="text-[11px] font-bold text-red-500">
                                        Please enter a valid 12-digit UAN number.
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Specialized Marital Status Section */}
                            {doc.subtype === 'marital_status' && !isUploaded && (
                              <div className="mt-3 sm:mt-4 sm:pl-7 space-y-3">
                                <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-3.5 sm:p-4 space-y-3 shadow-2xs">
                                  <label className="block text-xs font-bold text-slate-800">
                                    What is your marital status? <span className="text-red-500">*</span>
                                  </label>
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setDocumentNumbers((prev) => ({ ...prev, [key]: 'Single' }));
                                        autoUploadDoc(doc, null, 'Single').catch(() => { });
                                      }}
                                      className={`w-full py-2.5 px-3 rounded-xl text-xs font-bold transition border cursor-pointer text-center ${documentNumbers[key] === 'Single'
                                          ? 'bg-blue-600 border-blue-600 text-white shadow-xs'
                                          : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50 active:bg-slate-100'
                                        }`}
                                    >
                                      Single
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setDocumentNumbers((prev) => ({ ...prev, [key]: 'Married' }));
                                        autoUploadDoc(doc, null, 'Married').catch(() => { });
                                      }}
                                      className={`w-full py-2.5 px-3 rounded-xl text-xs font-bold transition border cursor-pointer text-center ${documentNumbers[key] === 'Married'
                                          ? 'bg-blue-600 border-blue-600 text-white shadow-xs'
                                          : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50 active:bg-slate-100'
                                        }`}
                                    >
                                      Married
                                    </button>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Specialized Complete Address Section */}
                            {doc.subtype === 'complete_address' && !isUploaded && (
                              <div className="mt-3 sm:mt-4 sm:pl-7 space-y-3">
                                <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-3.5 sm:p-4 space-y-3 shadow-2xs">
                                  <label className="block text-xs font-bold text-slate-800">
                                    Enter your Complete Address <span className="text-red-500">*</span>
                                  </label>
                                  <textarea
                                    rows={3}
                                    placeholder="Enter your full residential address..."
                                    value={documentNumbers[key] || ''}
                                    onChange={(e) => setDocumentNumbers((prev) => ({ ...prev, [key]: e.target.value }))}
                                    onBlur={() => {
                                      if (documentNumbers[key] && documentNumbers[key].trim().length > 5) {
                                        autoUploadDoc(doc, null, documentNumbers[key].trim()).catch(() => { });
                                      }
                                    }}
                                    disabled={isExpired}
                                    className={`block w-full rounded-xl border ${hasAttemptedSubmit && (!documentNumbers[key]?.trim() || documentNumbers[key]?.trim().length < 5)
                                        ? 'border-red-400 focus:border-red-500 focus:ring-red-500/20'
                                        : 'border-gray-200 focus:border-electric-blue focus:ring-electric-blue/20'
                                      } px-3.5 py-3 text-sm focus:outline-none focus:ring-2 bg-white shadow-2xs transition text-gray-900 resize-none`}
                                  />
                                </div>
                              </div>
                            )}

                            {/* Text Input Uploaded State Display */}
                            {isPanDeclarationDoc(doc) && isUploaded &&
                              uploadedDocs.find((d) => d.type === doc.type && d.subtype === doc.subtype)?.documentNumberKind === 'pan_acknowledgement' && (
                              <div className="mt-3 sm:pl-7">
                                <div className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-emerald-50 text-emerald-800 border border-emerald-200 text-xs font-semibold">
                                  <CheckCircleIcon className="h-4 w-4 text-emerald-600 shrink-0" />
                                  <span>
                                    {`Declared: PAN applied — Acknowledgement ${uploadedDocs.find((d) => d.type === doc.type && d.subtype === doc.subtype)?.documentNumber || documentNumbers[key] || 'Recorded'}`}
                                  </span>
                                </div>
                              </div>
                            )}

                            {['uan_number', 'marital_status', 'complete_address'].includes(doc.subtype) && isUploaded && (
                              <div className="mt-3 sm:pl-7">
                                <div className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-emerald-50 text-emerald-800 border border-emerald-200 text-xs font-semibold">
                                  <CheckCircleIcon className="h-4 w-4 text-emerald-600 shrink-0" />
                                  <span>
                                    {doc.subtype === 'uan_number'
                                      ? (uploadedDocs.find((d) => d.type === doc.type && d.subtype === doc.subtype)?.documentNumber === 'NOT_APPLICABLE'
                                        ? 'Declared: No UAN (Fresher / Non-EPFO)'
                                        : `Declared UAN: ${uploadedDocs.find((d) => d.type === doc.type && d.subtype === doc.subtype)?.documentNumber || documentNumbers[key] || 'Recorded'}`)
                                      : doc.subtype === 'marital_status'
                                      ? `Marital Status: ${uploadedDocs.find((d) => d.type === doc.type && d.subtype === doc.subtype)?.documentNumber || documentNumbers[key] || 'Recorded'}`
                                      : `Address: ${uploadedDocs.find((d) => d.type === doc.type && d.subtype === doc.subtype)?.documentNumber || documentNumbers[key] || 'Recorded'}`
                                    }
                                  </span>
                                </div>
                              </div>
                            )}

                            {/* 1. AI Live OCR Scanning State */}
                            {verifyingKeys.has(key) && (
                              <div className="mt-3 sm:pl-7">
                                <div className="flex items-center gap-3 rounded-2xl border border-blue-200 bg-blue-50/90 p-3.5 sm:p-4 text-xs text-blue-700 animate-pulse shadow-2xs">
                                  <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-blue-600 text-white shrink-0">
                                    <SparklesIcon className="h-4 w-4 sm:h-5 sm:w-5 animate-spin" />
                                  </div>
                                  <div>
                                    <p className="font-bold text-slate-900 text-xs sm:text-sm">
                                      Scanning document with AI OCR…
                                    </p>
                                    <p className="text-blue-700 text-[10px] sm:text-[11px] mt-0.5">
                                      Auto-extracting Name, ID Number, and validating authenticity. Please wait a moment.
                                    </p>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* 2. Verification result — problem, or the details we read */}
                            {/*
                              Bank documents are excluded outright. They own their panel
                              below, and this branch had no idea they existed: with no
                              `validation` on a bank response every bank upload fell
                              through to the generic "Invalid Document" card, including
                              the ones that passed. That card is gone; what remains here
                              renders whatever buildDocumentStatus decided, so the wording
                              and the check can no longer disagree.
                            */}
                            {!isBankDoc(doc) && verificationResults[key] && !verifyingKeys.has(key) && (() => {
                              const vRes = verificationResults[key];
                              const vVal = vRes?.validation;

                              const problem = key in docStatuses
                                ? docStatuses[key]
                                : buildDocumentStatus({ doc, result: vRes, candidateName: nameFromQuery });

                              if (problem) {
                                return (
                                  <DocumentStatusCard
                                    status={problem}
                                    disabled={isExpired}
                                    onAction={() => resetUploadFor(key)}
                                  />
                                );
                              }

                              if (!vVal) return null;

                                return (
                                  <div className="mt-3 sm:pl-7">
                                    <div className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-3.5 sm:p-4 space-y-3 shadow-2xs">
                                      <div className="flex flex-wrap items-center justify-between gap-2">
                                        <div className="flex items-center gap-2 text-emerald-900 font-bold text-xs">
                                          <div className="p-1 rounded-lg bg-emerald-100 text-emerald-700">
                                            <CheckCircleIcon className="h-4 w-4 shrink-0" />
                                          </div>
                                          <span>
                                            ID Authenticated ({String(vVal.docType || doc.label).toUpperCase().replace(/_/g, ' ')})
                                          </span>
                                        </div>
                                        <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200">
                                          <SparklesIcon className="h-3 w-3 text-emerald-600" />
                                          AI OCR Verified
                                        </span>
                                      </div>

                                      {doc.type === 'govt_id' && !isUploaded ? (
                                        <div className="pt-2 border-t border-emerald-100 space-y-3">
                                          <p className="text-[11px] text-emerald-800 font-medium mb-1">
                                            Please review and edit the details we found to ensure they match your document exactly.
                                          </p>
                                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                            <div>
                                              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">
                                                Name on ID
                                              </label>
                                              <input
                                                type="text"
                                                value={ocrEditableFields[key]?.name ?? ''}
                                                onChange={(e) => setOcrEditableFields(prev => ({
                                                  ...prev,
                                                  [key]: { ...prev[key], name: e.target.value }
                                                }))}
                                                disabled={isExpired}
                                                className="block w-full rounded-lg border border-emerald-200 focus:border-emerald-500 focus:ring-emerald-500/20 px-3 py-2 text-sm focus:outline-none focus:ring-2 bg-white transition"
                                              />
                                            </div>
                                            <div>
                                              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">
                                                Date of Birth
                                              </label>
                                              <input
                                                type="text"
                                                value={ocrEditableFields[key]?.dob ?? ''}
                                                onChange={(e) => setOcrEditableFields(prev => ({
                                                  ...prev,
                                                  [key]: { ...prev[key], dob: e.target.value }
                                                }))}
                                                disabled={isExpired}
                                                className="block w-full rounded-lg border border-emerald-200 focus:border-emerald-500 focus:ring-emerald-500/20 px-3 py-2 text-sm focus:outline-none focus:ring-2 bg-white transition"
                                              />
                                            </div>
                                          </div>
                                        </div>
                                      ) : (
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3 text-xs text-slate-700 pt-2 border-t border-emerald-100">
                                          {/* Always show Applicant Name for reference */}
                                          {nameFromQuery && (
                                            <div>
                                              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">
                                                Applicant Name
                                              </span>
                                              <span className="font-semibold text-slate-900 text-xs sm:text-sm">
                                                {nameFromQuery}
                                              </span>
                                            </div>
                                          )}
                                          {/* Show Name on ID — try normalizedData.name first, then rawExtractedFields.Name */}
                                          {(() => {
                                            const extractedName = vVal.normalizedData?.name
                                              || (vVal.rawExtractedFields?.Name || '').replace(/[^a-zA-Z\s]/g, ' ').replace(/\s+/g, ' ').trim()
                                              || '';
                                            return extractedName ? (
                                              <div>
                                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">
                                                  Name on ID
                                                </span>
                                                <span className="font-semibold text-slate-900 text-xs sm:text-sm">
                                                  {extractedName}
                                                </span>
                                              </div>
                                            ) : null;
                                          })()}
                                          {vVal.normalizedData?.id_number && (
                                            <div>
                                              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">
                                                Extracted ID Number
                                              </span>
                                              <span className="font-mono font-bold text-slate-900 text-xs sm:text-sm tracking-wide">
                                                {vVal.normalizedData.id_number}
                                              </span>
                                            </div>
                                          )}
                                          {vVal.normalizedData?.dob && (
                                            <div>
                                              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">
                                                Date of Birth
                                              </span>
                                              <span className="text-slate-800 font-medium">
                                                {vVal.normalizedData.dob}
                                              </span>
                                            </div>
                                          )}
                                          {(() => {
                                            const summary = nameMatchSummary(vRes.nameMatch);
                                            if (summary?.tone === 'review') {
                                              return (
                                                <div className="flex items-start gap-1.5 text-amber-800 font-medium sm:col-span-2 text-xs pt-1 border-t border-emerald-100/60">
                                                  <InformationCircleIcon className="h-4 w-4 text-amber-600 shrink-0 mt-px" />
                                                  <span>{summary.candidateText}</span>
                                                </div>
                                              );
                                            }
                                            if (summary?.tone === 'match') {
                                              return (
                                                <div className="flex items-center gap-1.5 text-emerald-700 font-semibold sm:col-span-2 text-xs pt-1 border-t border-emerald-100/60">
                                                  <CheckIcon className="h-4 w-4 text-emerald-600 shrink-0" strokeWidth={2.5} />
                                                  <span>{summary.candidateText} ({vRes.nameMatch?.comparedName || nameFromQuery || 'Candidate'})</span>
                                                </div>
                                              );
                                            }
                                            return null;
                                          })()}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                            })()}

                            {/* 3. Confirm ID Number */}
                            {doc.type === 'govt_id' && !isUploaded && fileSelected && !verifyingKeys.has(key) && (
                              <div className="mt-3 sm:mt-4 sm:pl-7 max-w-full sm:max-w-md space-y-1.5">
                                <div className="flex items-center justify-between">
                                  <label className="block text-xs font-bold text-gray-700">
                                    {doc.subtype === 'aadhaar_card'
                                      ? 'Aadhaar Card Number'
                                      : doc.subtype === 'pan_card'
                                        ? 'PAN Card Number'
                                        : doc.subtype === 'aadhaar_or_pan'
                                          ? 'Aadhaar or PAN Card Number'
                                          : doc.subtype === 'passport'
                                            ? 'Passport Number'
                                            : 'Driving Licence Number'}{' '}
                                    <span className="text-red-500">*</span>
                                  </label>
                                  {verificationResults[key]?.success && verificationResults[key]?.validation?.normalizedData?.id_number && (
                                    <span className="text-[10px] font-semibold text-emerald-600">
                                      Auto-filled from OCR
                                    </span>
                                  )}
                                </div>

                                <p className="text-[11px] text-slate-500 leading-snug">
                                  Please ensure this number matches your uploaded document before proceeding.
                                </p>

                                <input
                                  type="text"
                                  placeholder={
                                    doc.subtype === 'aadhaar_card'
                                      ? 'e.g. 12-digit Aadhaar number'
                                      : doc.subtype === 'pan_card'
                                        ? 'e.g. ABCDE1234F'
                                        : doc.subtype === 'aadhaar_or_pan'
                                          ? 'e.g. 12 digits or ABCDE1234F'
                                          : 'Enter document number'
                                  }
                                  value={documentNumbers[key] || ''}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    setDocumentNumbers((prev) => ({ ...prev, [key]: val }));
                                  }}
                                  disabled={isExpired}
                                  className={`block w-full rounded-xl border ${hasAttemptedSubmit && (
                                      !documentNumbers[key]?.trim() ||
                                      (doc.subtype === 'aadhaar_card' && !/^\d{12}$/.test(documentNumbers[key].replace(/\s+/g, ''))) ||
                                      (doc.subtype === 'pan_card' && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(documentNumbers[key].replace(/\s+/g, '').toUpperCase())) ||
                                      (doc.subtype === 'aadhaar_or_pan' && !/^\d{12}$/.test(documentNumbers[key].replace(/\s+/g, '').toUpperCase()) && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(documentNumbers[key].replace(/\s+/g, '').toUpperCase()))
                                    )
                                      ? 'border-red-400 focus:border-red-500 focus:ring-red-500/20'
                                      : 'border-gray-200 focus:border-electric-blue focus:ring-electric-blue/20'
                                    } px-3.5 py-3 sm:py-2.5 text-base sm:text-sm font-mono focus:outline-none focus:ring-2 bg-white shadow-2xs transition ${isExpired ? 'opacity-50 cursor-not-allowed text-gray-500' : 'text-gray-900'}`}
                                />

                                {hasAttemptedSubmit && (
                                  !documentNumbers[key]?.trim() ? (
                                    <p className="mt-1 text-[11px] font-bold text-red-500">
                                      Please confirm your {doc.label} number.
                                    </p>
                                  ) : doc.subtype === 'aadhaar_card' && !/^\d{12}$/.test(documentNumbers[key].replace(/\s+/g, '')) ? (
                                    <p className="mt-1 text-[11px] font-bold text-red-500">
                                      Must be a valid 12-digit Aadhaar Card number.
                                    </p>
                                  ) : doc.subtype === 'pan_card' && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(documentNumbers[key].replace(/\s+/g, '').toUpperCase()) ? (
                                    <p className="mt-1 text-[11px] font-bold text-red-500">
                                      Must be a valid 10-character PAN Card number (e.g. ABCDE1234F).
                                    </p>
                                  ) : doc.subtype === 'aadhaar_or_pan' && !/^\d{12}$/.test(documentNumbers[key].replace(/\s+/g, '').toUpperCase()) && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(documentNumbers[key].replace(/\s+/g, '').toUpperCase()) ? (
                                    <p className="mt-1 text-[11px] font-bold text-red-500">
                                      Must be a valid 12-digit Aadhaar OR PAN Card.
                                    </p>
                                  ) : null
                                )}
                              </div>
                            )}

                            {/*
                              3a. Bank problems.
                              Rendered outside the panel below because rejecting a bank
                              document clears the file that panel is conditioned on — so
                              a card nested in there would be removed by the very thing
                              it needs to report.
                            */}
                            {isBankDoc(doc) && !verifyingKeys.has(key) && (() => {
                              const problem = key in docStatuses
                                ? docStatuses[key]
                                : buildDocumentStatus({ doc, result: verificationResults[key], candidateName: nameFromQuery });
                              if (!problem) return null;
                              return (
                                <DocumentStatusCard
                                  status={problem}
                                  disabled={isExpired}
                                  onAction={() => resetUploadFor(key)}
                                />
                              );
                            })()}

                            {/* 3b. Bank account — every detail read off the document, confirmed or corrected by the candidate */}
                            {isBankDoc(doc) && !isUploaded && fileSelected && !verifyingKeys.has(key) && (() => {
                              const bank = verificationResults[key]?.bank;
                              const bankCheck = verificationResults[key]?.bankCheck;
                              // Not bankCheck.warnings: that list includes the ones the
                              // candidate can do nothing about. See BANK_WARNINGS.
                              const warnings = candidateWarningsFrom(bankCheck, bank);
                              const upload = () => {
                                if (!uploadingKeys.has(key) && isBankFormComplete(bankFormValue(key))) {
                                  autoUploadDoc(doc, selectedFiles[key], accountDigits(documentNumbers[key])).catch(() => {});
                                }
                              };

                              return (
                                <BankDetailsForm
                                  idPrefix={`bank-${key}`}
                                  token={token}
                                  value={bankFormValue(key)}
                                  onChange={(patch) => {
                                    if ('account' in patch) {
                                      setDocumentNumbers((prev) => ({ ...prev, [key]: patch.account }));
                                    }
                                    const { account: _account, ...rest } = patch;
                                    if (Object.keys(rest).length) {
                                      setBankFields((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), ...rest } }));
                                    }
                                  }}
                                  onCommit={upload}
                                  confirmed={!!bankConfirmations[key]}
                                  onConfirmChange={(isChecked) => {
                                    setBankConfirmations((prev) => ({ ...prev, [key]: isChecked }));
                                    if (isChecked) upload();
                                  }}
                                  readAccount={bank?.accountNumber}
                                  possiblyTruncated={!!bank?.possiblyTruncated}
                                  ifscSuggested={verificationResults[key]?.fields?.ifscSuggested ? bank?.ifscSuggested : null}
                                  warnings={warnings}
                                  hasAttemptedSubmit={hasAttemptedSubmit}
                                  disabled={isExpired}
                                  onView={() => openPreview(doc)}
                                />
                              );
                            })()}
                          </div>
                        </div>
                      );
                    })()}
                  </div>

                  {/* Right: security & document checklist */}
                  <div className="lg:col-span-4 space-y-4">
                    <div className="bg-slate-50 border border-gray-200 rounded-2xl p-4 sm:p-5 space-y-3">
                      <div className="flex items-start gap-2.5">
                        <ShieldCheckIcon className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
                        <div className="space-y-1">
                          <h4 className="text-xs font-bold uppercase tracking-wider text-gray-900">
                            Secure Data Collection
                          </h4>
                          <p className="text-[11px] text-gray-600 leading-relaxed">
                            Your files are encrypted and processed securely. Stored data is deleted automatically within 180 days.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Document checklist mini-summary */}
                    {requestedDocs.length > 1 && (
                      <div className="bg-white border border-gray-200 rounded-2xl p-4 sm:p-5 space-y-2.5">
                        <h4 className="text-xs font-bold uppercase tracking-wider text-gray-900">
                          Document Checklist
                        </h4>
                        <div className="space-y-1.5">
                          {requestedDocs.map((d, i) => {
                            const dk = `${d.type}::${d.subtype}`;
                            const isDone = uploadedKeys.has(dk);
                            const isCurrent = i === currentDocIndex;
                            return (
                              <button
                                key={dk}
                                type="button"
                                onClick={() => goToDocIndex(i)}
                                className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs transition-all cursor-pointer text-left ${
                                  isCurrent
                                    ? 'bg-blue-50 border border-blue-200 text-blue-900 font-bold'
                                    : isDone
                                      ? 'text-emerald-700 hover:bg-emerald-50'
                                      : 'text-gray-500 hover:bg-gray-50'
                                }`}
                              >
                                {isDone ? (
                                  <CheckCircleIcon className="h-4 w-4 text-emerald-500 shrink-0" />
                                ) : isCurrent ? (
                                  <div className="h-4 w-4 rounded-full border-2 border-blue-500 shrink-0 flex items-center justify-center">
                                    <div className="h-1.5 w-1.5 rounded-full bg-blue-500" />
                                  </div>
                                ) : (
                                  <div className="h-4 w-4 rounded-full border-2 border-gray-300 shrink-0" />
                                )}
                                <span className="truncate">{d.label}</span>
                                {d.required !== false && !isDone && (
                                  <span className="ml-auto text-[9px] font-bold text-red-500 shrink-0">REQ</span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/*
                  The step-level message.
                  It sits immediately above the controls that raise it, because that is
                  where somebody who just pressed Next is looking. As a toast it appeared
                  in the opposite corner of the screen, over the checklist, and timed out
                  on its own schedule rather than when the problem was fixed.
                */}
                {pageAlert && (
                  <div
                    ref={alertRef}
                    role="alert"
                    tabIndex={-1}
                    className="flex items-start gap-2.5 rounded-2xl border border-red-200 bg-red-50/80 px-4 py-3 focus:outline-none"
                  >
                    <ExclamationTriangleIcon className="h-5 w-5 text-red-600 shrink-0 mt-px" aria-hidden="true" />
                    <p className="text-xs sm:text-sm font-semibold text-red-900 leading-relaxed">
                      {pageAlert.message}
                    </p>
                  </div>
                )}

                {/* Footer navigation */}
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-5 border-t border-gray-100">
                  {/* Left: Back button */}
                  <div className="flex items-center gap-2">
                    {!isFirstDocStep && (
                      <button
                        type="button"
                        onClick={goToPrevDoc}
                        disabled={isTransitioning || isExpired}
                        className="inline-flex items-center gap-1.5 px-4 py-2.5 sm:py-2 rounded-xl border border-gray-300 bg-white hover:bg-gray-50 active:bg-gray-100 text-sm font-semibold text-gray-700 transition-all shadow-2xs disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                      >
                        <ChevronLeftIcon className="h-4 w-4" />
                        Back
                      </button>
                    )}

                    {verifyingKeys.size > 0 && (
                      <span className="text-xs text-blue-600 font-semibold animate-pulse flex items-center gap-1.5">
                        <SparklesIcon className="h-3.5 w-3.5 animate-spin" />
                        Scanning…
                      </span>
                    )}

                    {uploadingKeys.size > 0 && verifyingKeys.size === 0 && (
                      <span className="text-xs text-blue-600 font-semibold animate-pulse flex items-center gap-1.5">
                        <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />
                        Uploading…
                      </span>
                    )}
                  </div>

                  {/* Right: Next / Skip / Submit */}
                  <div className="flex items-center gap-2 w-full sm:w-auto">
                    {/* Skip for optional docs */}
                    {!isLastDocStep && requestedDocs[currentDocIndex]?.required === false && !uploadedKeys.has(`${requestedDocs[currentDocIndex]?.type}::${requestedDocs[currentDocIndex]?.subtype}`) && (
                      <button
                        type="button"
                        onClick={goToNextDoc}
                        disabled={isTransitioning || isExpired}
                        className="text-xs font-semibold text-gray-500 hover:text-gray-700 underline underline-offset-2 transition cursor-pointer px-2 py-1"
                      >
                        Skip (optional)
                      </button>
                    )}

                    {isLastDocStep || allDocsUploaded ? (
                      /* Submit button on last step */
                      <button
                        type="submit"
                        data-testid="document-submit-btn"
                        disabled={submittingAll || isExpired || verifyingKeys.size > 0 || uploadingKeys.size > 0}
                        className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-3.5 sm:py-2.5 text-sm sm:text-base font-bold text-white shadow-md hover:shadow-lg active:scale-98 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none transition-all cursor-pointer"
                      >
                        {isExpired ? (
                          "Time's up"
                        ) : submittingAll ? (
                          'Uploading Documents…'
                        ) : verifyingKeys.size > 0 ? (
                          <>
                            <SparklesIcon className="h-4 w-4 animate-spin text-white" />
                            Scanning…
                          </>
                        ) : toUpload.length > 0 ? (
                          requestedDocs[currentDocIndex]?.type === 'govt_id' && verificationResults[`${requestedDocs[currentDocIndex]?.type}::${requestedDocs[currentDocIndex]?.subtype}`] && !uploadedKeys.has(`${requestedDocs[currentDocIndex]?.type}::${requestedDocs[currentDocIndex]?.subtype}`) ? (
                            hasNextStep ? 'Confirm & Continue' : 'Confirm & Finish'
                          ) : hasNextStep ? 'Submit & Continue' : 'Submit & Finish'
                        ) : hasNextStep ? (
                          'Continue'
                        ) : (
                          'Finish'
                        )}
                        {!submittingAll && verifyingKeys.size === 0 && <ArrowRightIcon className="h-4 w-4" />}
                      </button>
                    ) : (
                      /* Next button on intermediate steps */
                      <button
                        type="button"
                        data-testid="document-next-btn"
                        onClick={handleNextStep}
                        disabled={isTransitioning || isExpired || verifyingKeys.size > 0 || uploadingKeys.size > 0}
                        className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-3.5 sm:py-2.5 text-sm sm:text-base font-bold text-white shadow-md hover:shadow-lg active:scale-98 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none transition-all cursor-pointer"
                      >
                        {verifyingKeys.size > 0 ? (
                          <>
                            <SparklesIcon className="h-4 w-4 animate-spin text-white" />
                            Scanning…
                          </>
                        ) : uploadingKeys.size > 0 ? (
                          'Uploading…'
                        ) : requestedDocs[currentDocIndex]?.type === 'govt_id' && verificationResults[`${requestedDocs[currentDocIndex]?.type}::${requestedDocs[currentDocIndex]?.subtype}`] && !uploadedKeys.has(`${requestedDocs[currentDocIndex]?.type}::${requestedDocs[currentDocIndex]?.subtype}`) ? (
                          'Confirm & Continue'
                        ) : (
                          'Next'
                        )}
                        <ChevronRightIcon className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
